import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Maximize2, Minimize2, RefreshCw, Loader2, 
  Heart, Star, Trophy, Skull, Play, Pause, Volume2, VolumeX,
  Eye, Gamepad2, Info, Home, Share2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import * as BABYLON from '@babylonjs/core';
import '@babylonjs/loaders';
import { buildObjectMaterial } from '../lib/material';
import { removeBackgroundIfNeeded, getCleanedSync } from '../lib/bgRemove';
import { roleSpriteDataUrl } from '../lib/roleSprites';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fixed-timestep schedulers keyed per scene — survives any scene.metadata replacement.
const fixedSteppers = new WeakMap();

export default function GamePlay() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [vrSupported, setVrSupported] = useState(false);
  const [inVR, setInVR] = useState(false);
  
  // Game state
  const [score, setScore] = useState(0);
  // Pre-game briefing gate: the game does not start until the player confirms they've
  // read the plot + how-to-play. Skipped inside the editor's mini playtest (embed).
  const startGateRef = useRef(true);
  const [startGate, setStartGate] = useState(true);
  // Embed mode (mini playtest iframe inside the editor) — hides nav so users can't navigate away
  const isEmbed = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embed') === '1';
  useEffect(() => { if (isEmbed) { startGateRef.current = false; setStartGate(false); grantSpawnGrace(); } }, [isEmbed]);

  // Fair-start guard: the player is briefly invulnerable after every spawn/level load,
  // so overlapping hazards or sniping enemies can never kill an untouched player instantly.
  const graceUntilRef = useRef(0);
  const grantSpawnGrace = (ms = 2500) => { graceUntilRef.current = performance.now() + ms; };
  const playerInvulnerable = () => performance.now() < graceUntilRef.current;

  // Asset preloader: every texture/sprite (across ALL stages) is fetched — and sprites
  // background-keyed — BEHIND the briefing gate, so the world renders instantly with
  // zero pop-in or texture flicker when the player presses start.
  const [assetsReady, setAssetsReady] = useState(false);
  const [loadPct, setLoadPct] = useState(0);
  useEffect(() => {
    if (!project) return;
    const gd = project.game_data || {};
    const all = [...(gd.objects || []), ...((gd.projectDefinition?.stages || []).flatMap(s => s.objects || []))];
    const spriteTypes = new Set(['player', 'enemy', 'npc', 'collectible', 'goal', 'sprite', 'obstacle', 'vehicle', 'decoration']);
    const urls = new Map(); // url -> needsKeying
    all.forEach(o => {
      if (o.asset_url) urls.set(o.asset_url, urls.get(o.asset_url) || spriteTypes.has(o.type));
      Object.values(o.textures || {}).forEach(u => { if (typeof u === 'string' && u && !urls.has(u)) urls.set(u, false); });
    });
    const list = [...urls.entries()];
    if (!list.length) { setLoadPct(100); setAssetsReady(true); return; }
    let done = 0;
    const bump = () => { done += 1; setLoadPct(Math.round((done / list.length) * 100)); };
    const jobs = list.map(([u, key]) => new Promise(res => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      const finish = () => { bump(); res(); };
      img.onload = () => { key ? removeBackgroundIfNeeded(u).catch(() => {}).then(finish, finish) : finish(); };
      img.onerror = finish;
      img.src = u;
    }));
    const cap = new Promise(res => setTimeout(res, 8000)); // never hold the player hostage
    Promise.race([Promise.all(jobs), cap]).then(() => { setLoadPct(100); setAssetsReady(true); });
  }, [project]);
  const [health, setHealth] = useState(100);
  // Runtime tuning — read from game_data.settings after project loads so designers can tune per-game.
  const startingHealthRef = useRef(100);
  const winScoreRef = useRef(0);
  const [collectiblesCount, setCollectiblesCount] = useState(0);
  const [totalCollectibles, setTotalCollectibles] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [buffActive, setBuffActive] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [stageBanner, setStageBanner] = useState(null);
  const [bossInfo, setBossInfo] = useState(null);
  const [bossIntro, setBossIntro] = useState(null);
  const stageIdxRef = useRef(0);
  const stageClearingRef = useRef(false);
  const pdRef = useRef(null);
  const surviveStartRef = useRef(0);
  const isMutedRef = useRef(false);
  const musicStopRef = useRef(null);
  const telemetrySentRef = useRef({});
  const playStartRef = useRef(null);
  const [winElapsed, setWinElapsed] = useState(0);
  // One input abstraction (spec 2.6): keyboard, canvas taps and on-screen touch buttons
  // all write the same logical action map read by the fixed-step physics loop.
  const inputMapRef = useRef({});
  const isTouch = typeof window !== 'undefined' && (navigator.maxTouchPoints > 0 || 'ontouchstart' in window);
  const touchHold = (key) => ({
    onPointerDown: (e) => { e.preventDefault(); inputMapRef.current[key] = true; },
    onPointerUp: () => { inputMapRef.current[key] = false; },
    onPointerLeave: () => { inputMapRef.current[key] = false; },
    onPointerCancel: () => { inputMapRef.current[key] = false; },
    onContextMenu: (e) => e.preventDefault(),
  });

  const telemetry = (event) => {
    try { axios.post(`${API}/telemetry/event`, { project_id: projectId, event }).catch(() => {}); } catch { /* noop */ }
  };

  const camOverrideRef = useRef(null);
  const [camMode, setCamMode] = useState('auto');
  const cycleCamera = () => {
    const modes = ['2d', 'follow', 'third_person', 'isometric'];
    const cur = camOverrideRef.current || project?.game_data?.settings?.camera_type || '2d';
    const next = modes[(modes.indexOf(cur) + 1) % modes.length];
    camOverrideRef.current = next;
    setCamMode(next);
    if (engineRef.current) engineRef.current.dispose();
    initGame();
    toast.info(`Camera: ${next.replace('_', ' ')}`);
  };

  const streakDoneRef = useRef(false);
  const checkStreak = async () => {
    if (streakDoneRef.current || !userRef.current) return; // guests have no streak — skip the call entirely
    streakDoneRef.current = true;
    try {
      const r = await axios.post(`${API}/streak/checkin`, {}, { withCredentials: true });
      if (r.data.new_day && r.data.bonus > 0) {
        setScore(prev => prev + r.data.bonus);
        toast.success(`🔥 Day ${r.data.streak} play streak — +${r.data.bonus} bonus points!`, { duration: 5000 });
      }
    } catch { /* guests have no streak */ }
  };
  
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const sceneRef = useRef(null);
  const vrHelperRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (authLoading) return; // wait for auth so we hit the right endpoint first (no guest 401 noise)
    fetchProject();
  }, [projectId, authLoading]);

  useEffect(() => {
    if (!canvasRef.current || !project) return;

    let cleanupFn = null;
    initGame().then((c) => { cleanupFn = c; }).catch((err) => {
      console.error('Game init failed:', err);
      toast.error('Failed to load game: ' + (err?.message || 'unknown'));
    });

    return () => {
      if (typeof cleanupFn === 'function') cleanupFn();
      if (musicStopRef.current) { musicStopRef.current(); musicStopRef.current = null; }
      stopWeatherAmbience();
      if (engineRef.current) {
        engineRef.current.dispose();
      }
    };
  }, [project]);

  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);

  useEffect(() => {
    if (gameWon && !telemetrySentRef.current.won) {
      telemetrySentRef.current.won = true;
      telemetry('game_won');
      setWinElapsed(Math.max(1, Math.round((performance.now() - (playStartRef.current || performance.now())) / 1000)));
    }
    if (gameOver && !telemetrySentRef.current.lost) { telemetrySentRef.current.lost = true; telemetry('game_lost'); }
    if ((gameWon || gameOver) && musicStopRef.current) { musicStopRef.current(); musicStopRef.current = null; }
    if (gameWon || gameOver) stopWeatherAmbience();
  }, [gameWon, gameOver]);

  const initGame = async () => {
    grantSpawnGrace(); // fair start: brief invulnerability on every level load/restart
    const canvas = canvasRef.current;
    const engine = new BABYLON.Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true });
    // Re-measure after layout settles — prevents a dead band at the bottom of the canvas
    requestAnimationFrame(() => engine.resize());
    setTimeout(() => { try { engine.resize(); } catch { /* disposed */ } }, 350);
    const ro = new ResizeObserver(() => { try { engine.resize(); } catch { /* disposed */ } });
    ro.observe(canvas.parentElement || canvas);
    engine.onDisposeObservable.add(() => ro.disconnect());
    engineRef.current = engine;

    const scene = new BABYLON.Scene(engine);
    sceneRef.current = scene;

    // Enable physics only if the Ammo global is available (loaded lazily to avoid huge bundle).
    // Games render fine without physics — enemies + collectibles still work via manual updates.
    const gravityValue = project.game_data?.settings?.gravity ?? -9.81;
    const gravityVector = new BABYLON.Vector3(0, gravityValue, 0);
    // Apply designer-tuned starting health + score-to-win.
    const startHP = project.game_data?.settings?.starting_health ?? 100;
    startingHealthRef.current = startHP;
    setHealth(startHP);
    winScoreRef.current = project.game_data?.settings?.win_score ?? 0;
    let physicsEnabled = false;
    try {
      if (typeof window !== 'undefined' && window.Ammo) {
        scene.enablePhysics(gravityVector, new BABYLON.AmmoJSPlugin());
        physicsEnabled = true;
      } else if (BABYLON.CannonJSPlugin && typeof window !== 'undefined' && window.CANNON) {
        scene.enablePhysics(gravityVector, new BABYLON.CannonJSPlugin());
        physicsEnabled = true;
      }
      // If no physics engine is available, the scene still renders and manual gravity in the
      // player-controls path keeps gameplay functional.
    } catch (physicsErr) {
      // Never let physics init break the render loop
      console.warn('Physics unavailable — running without physics engine:', physicsErr?.message);
    }
    scene.metadata = { ...(scene.metadata || {}), physicsEnabled };

    // Atmospheric settings based on genre
    const genre = project.genre || 'platformer';
    setupAtmosphere(scene, genre, project.game_data?.settings?.sky_color,
      project.game_data?.projectDefinition?.stages?.[stageIdxRef.current]);
    const wkind = project.game_data?.projectDefinition?.stages?.[stageIdxRef.current]?.weather ||
      project.game_data?.settings?.weather || null;
    setupWeather(scene, wkind);
    startWeatherAmbience(wkind);

    // Camera setup
    const camSettings = { ...(project.game_data?.settings || {}) };
    if (camOverrideRef.current) camSettings.camera_type = camOverrideRef.current;
    const camera = setupCamera(scene, canvas, camSettings);

    // Mouse wheel / pinch zoom in preview
    const onWheel = (e) => {
      e.preventDefault();
      const d = e.deltaY > 0 ? 1.1 : 0.9;
      if (camera.mode === BABYLON.Camera.ORTHOGRAPHIC_CAMERA) {
        camera.orthoTop *= d; camera.orthoBottom *= d; camera.orthoLeft *= d; camera.orthoRight *= d;
      } else if (camera.radius !== undefined) {
        camera.radius = Math.max(4, Math.min(45, camera.radius * d));
      }
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    scene.onDisposeObservable.add(() => canvas.removeEventListener('wheel', onWheel));
    
    // Lighting
    setupLighting(scene, genre, project?.game_data?.settings || {});

    // Post-processing pipeline: bloom + FXAA + optional color grading via game settings.
    try {
      const pipe = new BABYLON.DefaultRenderingPipeline('playmold_pipe', true, scene, [camera]);
      pipe.fxaaEnabled = project.game_data?.settings?.antialias !== false; // default on
      const bloomSetting = project.game_data?.settings?.bloom;
      pipe.bloomEnabled = bloomSetting === undefined ? true : !!bloomSetting;
      pipe.bloomIntensity = Number.isFinite(project.game_data?.settings?.bloom_intensity)
        ? project.game_data.settings.bloom_intensity : 0.4;
      pipe.bloomThreshold = 0.85;
      pipe.imageProcessing.contrast = 1.12;
      pipe.imageProcessing.exposure = 1.0;
      // Filmic tone mapping + sharpen — the "engine grade" look on every game
      pipe.imageProcessing.toneMappingEnabled = true;
      pipe.imageProcessing.toneMappingType = BABYLON.ImageProcessingConfiguration.TONEMAPPING_ACES;
      pipe.sharpenEnabled = true;
      pipe.sharpen.edgeAmount = 0.18;
      pipe.imageProcessing.vignetteEnabled = !!project.game_data?.settings?.vignette;
    } catch (e) { /* rendering pipeline can fail on very low-end WebGL — game still plays */ }

    // Store camera on scene for the parallax update loop below.
    scene.metadata = { ...(scene.metadata || {}), mainCamera: camera };

    // WebXR / AR — user opt-in via game settings. Adds a small VR button to the canvas.
    (async () => {
      if (project.game_data?.settings?.vr_enabled || project.game_data?.settings?.ar_enabled) {
        try {
          const mode = project.game_data.settings.ar_enabled ? 'immersive-ar' : 'immersive-vr';
          const xr = await scene.createDefaultXRExperienceAsync({
            uiOptions: { sessionMode: mode, referenceSpaceType: 'local-floor' },
          });
          if (xr && xr.baseExperience) {
            scene.metadata.xr = xr;
          }
        } catch (e) {
          // WebXR unsupported — silently continue without XR.
        }
      }
    })();

    // Create game world — stage-aware: stage 0 uses game_data.objects; later stages use
    // the pre-materialized objects stored in projectDefinition.stages[i].objects.
    const pd = project.game_data?.projectDefinition || null;
    pdRef.current = pd;
    const stageObjs = (stageIdxRef.current > 0 && pd?.stages?.[stageIdxRef.current]?.objects)
      ? pd.stages[stageIdxRef.current].objects
      : (project.game_data?.objects || []);
    const gameObjects = await createGameWorld(scene, stageObjs, genre);
    
    // Setup player with proper physics
    const player = gameObjects.player;
    playerMeshRef.current = player || null;
    coopRef.current = []; // scene rebuilt — co-op pads re-claim on next input
    // Level-intro storyline (stage 0) — later stages show theirs on advance.
    if (stageIdxRef.current === 0 && !introShownRef.current && pd?.stages?.[0]?.story) {
      introShownRef.current = true;
      setStageBanner({ title: pd.stages[0].name || 'Level 1', story: pd.stages[0].story });
      setTimeout(() => setStageBanner(null), 4200);
    }
    if (player) {
      scene.metadata = { ...(scene.metadata || {}), playerMesh: player };
      setupPlayerControls(scene, player, engine, project.game_data?.settings, gameObjects);
      setupCollisionDetection(scene, player, gameObjects);
      setupPlayerCombat(scene, player, gameObjects, project.game_data?.settings || {});
    }

    // Boss HUD — armed games have a boss guarding the final stage
    const bossE = gameObjects.enemies.find(e => e.config?.boss);
    setBossInfo(bossE ? { name: bossE.config.name || 'Boss', hp: bossE.config.hp || 150, max: bossE.config.hp || 150 } : null);
    if (bossE) {
      // Intro moment: screen shake + name reveal
      setBossIntro(bossE.config.name || 'Boss');
      playEnemyCue('summon');
      setTimeout(() => playEnemyCue('chase'), 500);
      setTimeout(() => setBossIntro(null), 2800);
    } else {
      setBossIntro(null);
    }

    // Telemetry + survive-objective timer + synthesized score
    if (!playStartRef.current) playStartRef.current = performance.now();
    if (!telemetrySentRef.current.started) { telemetrySentRef.current.started = true; telemetry('play_started'); checkStreak(); }
    surviveStartRef.current = performance.now();
    startMusic(pd?.stages?.[stageIdxRef.current]?.music || pd?.audio?.music_profile || null, genre);

    // Setup enemy AI
    setupEnemyAI(scene, gameObjects.enemies, player, gameObjects);
    setupSpawners(scene, gameObjects, player);

    // VR Support
    // VR/AR: only when the designer explicitly opts in via game settings.
    if (project.game_data?.settings?.vr_enabled || project.game_data?.settings?.ar_enabled) {
      await setupVR(scene, canvas);
    }

    // Handle window resize
    const handleResize = () => engine.resize();
    window.addEventListener('resize', handleResize);

    // Parallax background follower: any mesh tagged parallax follows the camera on the X/Y plane.
    const parallaxMeshes = scene.meshes.filter(m => m.metadata?.parallax);
    const parallaxOrigin = new Map(parallaxMeshes.map(m => [m.name, m.position.clone()]));

    // ---- Tilemaps: paint 2D grid-authored levels as real cubes in the scene ----
    (project.game_data?.tilemaps || []).forEach((tm, tmIdx) => {
      const cellSize = tm.cell_size || 1;
      const paletteMap = new Map((tm.palette || []).map(p => [p.id, p]));
      (tm.cells || []).forEach((row, r) => {
        row.forEach((v, c) => {
          if (!v) return;
          const pal = paletteMap.get(v);
          if (!pal) return;
          const cube = BABYLON.MeshBuilder.CreateBox(`tile_${tmIdx}_${r}_${c}`, { size: cellSize }, scene);
          // Center the grid at world origin: (c - cols/2) * cellSize
          cube.position = new BABYLON.Vector3((c - (tm.cols || 0) / 2) * cellSize, cellSize / 2, ((tm.rows || 0) / 2 - r) * cellSize);
          cube.checkCollisions = true;
          const mat = new BABYLON.StandardMaterial(`tile_mat_${v}`, scene);
          mat.diffuseColor = BABYLON.Color3.FromHexString(pal.color || '#4B5563');
          if (pal.url) mat.diffuseTexture = new BABYLON.Texture(pal.url, scene);
          cube.material = mat;
        });
      });
    });

    // ---- Compile & run object scripts / behaviors ----
    // Resolve the player mesh once. Look at game_data first (any object with type='player'),
    // then fall back to common names like Player_body / player_capsule.
    const playerObj = (project.game_data?.objects || []).find(o => o.type === 'player');
    const playerMeshName = playerObj?.name || null;
    const findPlayerMesh = () => {
      if (playerMeshName) {
        const m = scene.getMeshByName(playerMeshName);
        if (m) return m;
      }
      return scene.getMeshByName('Player_body')
        || scene.getMeshByName('player_capsule')
        || scene.getMeshByName('player')
        || scene.getMeshByName('Player')
        || scene.meshes.find(m => m.metadata?.isPlayer)
        || scene.meshes.find(m => (m.name || '').toLowerCase().includes('player'));
    };
    const compiledScripts = [];
    (project.game_data?.objects || []).forEach(obj => {
      (obj.scripts || []).forEach(s => {
        if (!s.compiled) return;
        try {
          // eslint-disable-next-line no-new-func
          const fn = new Function('ctx', s.compiled);
          compiledScripts.push({ objName: obj.name, fn });
        } catch { /* invalid snippet, skip */ }
      });
    });

    const sceneStart = performance.now();
    let lastFrame = sceneStart;
    scene.onBeforeRenderObservable.add(() => {
      const cam = scene.metadata?.mainCamera;
      if (cam) {
        for (const m of parallaxMeshes) {
          const origin = parallaxOrigin.get(m.name);
          if (!origin) continue;
          m.position.x = origin.x + cam.position.x * 0.7;
          m.position.y = origin.y + cam.position.y * 0.7;
        }
      }
      // Execute compiled behavior scripts every frame with a bounded execution helper.
      if (compiledScripts.length > 0) {
        const now = performance.now();
        const dt = Math.min(0.1, (now - lastFrame) / 1000);
        lastFrame = now;
        const timeSec = (now - sceneStart) / 1000;
        for (const { objName, fn } of compiledScripts) {
          const mesh = scene.getMeshByName(objName);
          if (!mesh) continue;
          const playerMesh = findPlayerMesh();
          const ctx = {
            self: {
              position: [mesh.position.x, mesh.position.y, mesh.position.z],
              scale: [mesh.scaling.x, mesh.scaling.y, mesh.scaling.z],
              velocity: mesh.metadata?.velocity || [0, 0, 0],
              color: mesh.material?.diffuseColor?.toHexString?.() || '#FFFFFF',
            },
            player: playerMesh ? {
              position: [playerMesh.position.x, playerMesh.position.y, playerMesh.position.z],
              hp: 100,
            } : { position: [0, 0, 0], hp: 100 },
            dt,
            time: timeSec,
            moveToward: (target, speed = 1) => {
              const t = Array.isArray(target) ? target : (target?.position || null);
              if (!t || t.length < 3) return;
              const dx = t[0] - mesh.position.x, dy = t[1] - mesh.position.y, dz = t[2] - mesh.position.z;
              const mag = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
              mesh.position.x += (dx / mag) * speed * dt;
              mesh.position.y += (dy / mag) * speed * dt;
              mesh.position.z += (dz / mag) * speed * dt;
            },
            rotate: (axis, rad = 0.1) => {
              // Accept 'x'|'y'|'z' string OR a 3-vector like [0,1,0].
              if (typeof axis === 'string') {
                const key = axis.toLowerCase();
                if (key === 'y') mesh.rotation.y += rad;
                else if (key === 'x') mesh.rotation.x += rad;
                else if (key === 'z') mesh.rotation.z += rad;
              } else if (Array.isArray(axis) && axis.length >= 3) {
                mesh.rotation.x += (axis[0] || 0) * rad;
                mesh.rotation.y += (axis[1] || 0) * rad;
                mesh.rotation.z += (axis[2] || 0) * rad;
              }
            },
            setColor: (hex) => {
              if (typeof hex !== 'string') return;
              try {
                const col = BABYLON.Color3.FromHexString(hex);
                if (mesh.material?.diffuseColor) mesh.material.diffuseColor = col;
                else if (mesh.material?.albedoColor) mesh.material.albedoColor = col;
              } catch (_) { /* invalid hex — silently skip */ }
            },
            spawn: () => { /* stub — server-side authored spawns handled elsewhere */ },
            destroy: () => { if (mesh.dispose) mesh.dispose(); },
            damagePlayer: (n) => { if (typeof n === 'number') setHealth(prev => Math.max(0, prev - n)); },
            emitParticle: () => { /* stub — attach a fire-and-forget particle system */ },
            // Legacy aliases the LLM sometimes emits:
            translate: (dx = 0, dy = 0, dz = 0) => {
              mesh.position.x += dx * dt; mesh.position.y += dy * dt; mesh.position.z += dz * dt;
            },
            scaleBy: (f = 1) => {
              mesh.scaling.x *= f; mesh.scaling.y *= f; mesh.scaling.z *= f;
            },
          };
          try { fn(ctx); } catch { /* silently skip broken scripts each frame */ }
        }
      }
    });

    // Render loop
    engine.runRenderLoop(() => {
      if (!isPaused && !gameOver && !gameWon) {
        scene.render();
      }
    });

    // Count collectibles
    setTotalCollectibles(gameObjects.collectibles?.length || 0);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  };

  const setupAtmosphere = (scene, genre, skyColor, stageMeta) => {
    switch (genre) {
      case 'puzzle':
        scene.clearColor = new BABYLON.Color4(0.1, 0.05, 0.15, 1);
        scene.fogMode = BABYLON.Scene.FOGMODE_EXP2;
        scene.fogDensity = 0.01;
        scene.fogColor = new BABYLON.Color3(0.1, 0.05, 0.15);
        break;
      case 'fps':
        scene.clearColor = new BABYLON.Color4(0.2, 0.3, 0.4, 1);
        break;
      case 'rpg':
        scene.clearColor = new BABYLON.Color4(0.1, 0.15, 0.2, 1);
        scene.fogMode = BABYLON.Scene.FOGMODE_LINEAR;
        scene.fogStart = 20;
        scene.fogEnd = 100;
        scene.fogColor = new BABYLON.Color3(0.5, 0.6, 0.7);
        break;
      default:
        scene.clearColor = new BABYLON.Color4(0.05, 0.05, 0.07, 1);
    }
    // Designer sky override — per-level (stage.sky_color) beats global settings.sky_color.
    const hex = stageMeta?.sky_color || skyColor;
    if (hex) {
      try {
        const c3 = BABYLON.Color3.FromHexString(hex);
        scene.clearColor = new BABYLON.Color4(c3.r, c3.g, c3.b, 1);
        if (scene.fogMode !== BABYLON.Scene.FOGMODE_NONE) scene.fogColor = c3;
      } catch { /* invalid hex — keep genre default */ }
    }
  };

  // Per-level weather — designer option (stage.weather: rain | snow | embers).
  // The emitter follows the camera so the effect covers wherever the player goes.
  const setupWeather = (scene, kind) => {
    if (!kind || !['rain', 'snow', 'embers'].includes(kind)) return;
    const dt = new BABYLON.DynamicTexture('weatherTex', { width: 32, height: 32 }, scene, false);
    const ctx = dt.getContext();
    const grad = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 32, 32);
    dt.update();
    const ps = new BABYLON.ParticleSystem('weather', kind === 'rain' ? 900 : 420, scene);
    ps.particleTexture = dt;
    const emitter = new BABYLON.Vector3(0, 18, 0);
    ps.emitter = emitter;
    ps.minEmitBox = new BABYLON.Vector3(-35, 0, -25);
    ps.maxEmitBox = new BABYLON.Vector3(35, 5, 25);
    if (kind === 'rain') {
      ps.color1 = new BABYLON.Color4(0.6, 0.75, 1, 0.55);
      ps.color2 = new BABYLON.Color4(0.5, 0.65, 1, 0.35);
      ps.minSize = 0.05; ps.maxSize = 0.12;
      ps.minScaleY = 6; ps.maxScaleY = 10;
      ps.direction1 = new BABYLON.Vector3(-0.5, -30, 0);
      ps.direction2 = new BABYLON.Vector3(0.5, -40, 0);
      ps.minLifeTime = 0.6; ps.maxLifeTime = 1.0;
      ps.emitRate = 650;
    } else if (kind === 'snow') {
      ps.color1 = new BABYLON.Color4(1, 1, 1, 0.9);
      ps.color2 = new BABYLON.Color4(0.9, 0.95, 1, 0.7);
      ps.minSize = 0.08; ps.maxSize = 0.22;
      ps.direction1 = new BABYLON.Vector3(-1.2, -2.5, -0.6);
      ps.direction2 = new BABYLON.Vector3(1.2, -1.5, 0.6);
      ps.minLifeTime = 6; ps.maxLifeTime = 12;
      ps.emitRate = 130;
    } else {
      ps.color1 = new BABYLON.Color4(1, 0.55, 0.15, 0.9);
      ps.color2 = new BABYLON.Color4(1, 0.3, 0.05, 0.6);
      ps.colorDead = new BABYLON.Color4(0.3, 0.05, 0, 0);
      ps.minSize = 0.06; ps.maxSize = 0.18;
      ps.direction1 = new BABYLON.Vector3(-0.6, 1.5, -0.3);
      ps.direction2 = new BABYLON.Vector3(0.6, 3.5, 0.3);
      ps.minLifeTime = 2.5; ps.maxLifeTime = 5;
      ps.emitRate = 90;
      ps.blendMode = BABYLON.ParticleSystem.BLENDMODE_ADD;
    }
    scene.onBeforeRenderObservable.add(() => {
      const cam = scene.activeCamera;
      if (!cam) return;
      emitter.x = cam.position.x;
      emitter.z = cam.position.z;
      emitter.y = kind === 'embers' ? cam.position.y - 5 : cam.position.y + 14;
    });
    ps.start();
  };

  const setupCamera = (scene, canvas, settings) => {
    const cameraType = settings?.camera_type || 'follow';
    let camera;

    switch (cameraType) {
      case '2d':
      case 'orthographic': {
        // Orthographic side-scroll camera that FOLLOWS the player. Player faces +Z, world lies on XY plane.
        camera = new BABYLON.FreeCamera('camera', new BABYLON.Vector3(0, 5, -20), scene);
        camera.rotation = new BABYLON.Vector3(0, 0, 0); // face +Z, fixed orientation
        camera.mode = BABYLON.Camera.ORTHOGRAPHIC_CAMERA;
        const ortho = settings?.ortho_size || 8;
        const aspect = (canvas.clientWidth || 16) / (canvas.clientHeight || 9);
        camera.orthoTop = ortho;
        camera.orthoBottom = -ortho;
        camera.orthoLeft = -ortho * aspect;
        camera.orthoRight = ortho * aspect;
        scene.onBeforeRenderObservable.add(() => {
          const pl = scene.metadata?.playerMesh;
          if (!pl || pl.isDisposed()) return;
          camera.position.x += (pl.position.x - camera.position.x) * 0.12;
          camera.position.y += (pl.position.y + 2 - camera.position.y) * 0.08;
        });
        break;
      }
      case 'first_person':
        camera = new BABYLON.UniversalCamera("camera", new BABYLON.Vector3(0, 2, -5), scene);
        camera.speed = 0.5;
        camera.angularSensibility = 1000;
        break;
      case 'third_person':
        camera = new BABYLON.ArcRotateCamera("camera", -Math.PI / 2, Math.PI / 3, 15, BABYLON.Vector3.Zero(), scene);
        camera.lowerRadiusLimit = 5;
        camera.upperRadiusLimit = 30;
        break;
      case 'isometric':
        camera = new BABYLON.ArcRotateCamera("camera", -Math.PI / 4, Math.PI / 4, 30, BABYLON.Vector3.Zero(), scene);
        camera.lowerBetaLimit = Math.PI / 4;
        camera.upperBetaLimit = Math.PI / 4;
        break;
      default: // follow camera
        camera = new BABYLON.FollowCamera("camera", new BABYLON.Vector3(0, 10, -20), scene);
        camera.radius = 15;
        camera.heightOffset = 8;
        camera.rotationOffset = 180;
        camera.cameraAcceleration = 0.05;
        camera.maxCameraSpeed = 10;
    }

    camera.attachControl(canvas, true);
    // Ensure the canvas takes focus so mouse "look around" works on published games.
    try {
      canvas.setAttribute('tabindex', '0');
      canvas.style.outline = 'none';
      canvas.style.cursor = 'grab';
      canvas.addEventListener('mousedown', () => { canvas.style.cursor = 'grabbing'; canvas.focus(); });
      canvas.addEventListener('mouseup',   () => { canvas.style.cursor = 'grab'; });
    } catch { /* ignore */ }
    return camera;
  };

  const setupLighting = (scene, genre, settings = {}) => {
    // User-controlled environment lighting: multiplier + optional tint (no extra
    // auto-lights are ever added — light objects are explicit user tools).
    const lightMult = Math.max(0, settings.ambient_intensity ?? 1);
    const ambient = new BABYLON.HemisphericLight("ambient", new BABYLON.Vector3(0, 1, 0), scene);
    ambient.intensity = 0.4 * lightMult;
    if (settings.light_color) { try { ambient.diffuse = BABYLON.Color3.FromHexString(settings.light_color); } catch { /* invalid hex */ } }

    // Main directional light
    const sun = new BABYLON.DirectionalLight("sun", new BABYLON.Vector3(-1, -2, -1), scene);
    sun.intensity = 0.8 * lightMult;

    // Enable shadows
    const shadowGenerator = new BABYLON.ShadowGenerator(1024, sun);
    shadowGenerator.useBlurExponentialShadowMap = true;
    shadowGenerator.blurKernel = 32;

    // Genre-specific lighting
    if (genre === 'puzzle') {
      ambient.diffuse = new BABYLON.Color3(0.5, 0.3, 0.8);
      sun.diffuse = new BABYLON.Color3(0.8, 0.5, 1);
    }

    return { ambient, sun, shadowGenerator };
  };

  const createGameWorld = async (scene, objects, genre) => {
    const gameObjects = {
      player: null,
      enemies: [],
      collectibles: [],
      platforms: [],
      goals: [],
      triggers: []
    };

    // Create ground with texture
    const ground = BABYLON.MeshBuilder.CreateGround("mainGround", { width: 100, height: 100 }, scene);
    const groundMat = new BABYLON.StandardMaterial("groundMat", scene);
    groundMat.diffuseColor = new BABYLON.Color3(0.2, 0.3, 0.2);
    groundMat.specularColor = new BABYLON.Color3(0.1, 0.1, 0.1);
    ground.material = groundMat;
    ground.receiveShadows = true;
    if (scene.metadata?.physicsEnabled) {

      ground.physicsImpostor = new BABYLON.PhysicsImpostor(ground, BABYLON.PhysicsImpostor.BoxImpostor, { mass: 0, friction: 0.8, restitution: 0.2 }, scene);

    }

    // Role vocabulary → runtime behavior. Users mark characters as hero / anti-hero /
    // anti-villain / villain / weapon / trophy and they perform those roles in preview.
    const normalizeRole = (obj) => {
      const r = (obj.role || '').toLowerCase().replace('-', '_');
      if (obj.solid_kind === 'background') return { ...obj, type: 'background' };
      if (r === 'hero') return { ...obj, type: 'player' };
      if (r === 'villain') return { ...obj, type: 'enemy', behavior: obj.behavior || 'chase' };
      if (r === 'anti_villain') return { ...obj, type: 'enemy', behavior: obj.behavior || 'patrol', damage: Math.min(obj.damage ?? 5, 5) };
      if (r === 'anti_hero') return { ...obj, type: 'enemy', behavior: 'patrol', damage: 0, friendly: true, color: obj.color || '#38BDF8' };
      if (r === 'weapon' && obj.type !== 'collectible') return { ...obj, type: 'collectible', pickup: 'weapon' };
      if (r === 'weapon') return { ...obj, pickup: 'weapon' };
      if (r === 'trophy') return { ...obj, type: 'collectible', pickup: 'trophy' };
      return obj;
    };

    for (const rawObj of objects) {
      const obj = normalizeRole(rawObj);
      const mesh = await createGameObject(scene, obj, genre);
      if (!mesh) continue;

      // Categorize objects
      switch (obj.type) {
        case 'player':
          gameObjects.player = mesh;
          // Attach follow camera to player
          const followCam = scene.getCameraByName("camera");
          if (followCam && followCam.lockedTarget === undefined) {
            followCam.lockedTarget = mesh;
          }
          break;
        case 'enemy':
          gameObjects.enemies.push({ mesh, config: obj });
          break;
        case 'collectible':
          gameObjects.collectibles.push({ mesh, config: obj, collected: false });
          break;
        case 'platform':
        case 'floor':
        case 'wall':
          gameObjects.platforms.push(mesh);
          break;
        case 'goal':
          gameObjects.goals.push(mesh);
          break;
        case 'trigger':
          gameObjects.triggers.push({ mesh, config: obj });
          break;
        case 'spawner':
          // Spawner metadata — waves get emitted from setupSpawners()
          gameObjects.spawners = gameObjects.spawners || [];
          gameObjects.spawners.push({ mesh, config: obj });
          break;
        case 'pressure_plate':
          gameObjects.plates = gameObjects.plates || [];
          gameObjects.plates.push({ mesh, config: obj, activated: false });
          break;
        case 'door':
          gameObjects.doors = gameObjects.doors || [];
          gameObjects.doors.push({ mesh, config: obj, open: false, initialY: (obj.position || [0,1,0])[1] });
          break;
        case 'climbable':
          gameObjects.climbables = gameObjects.climbables || [];
          gameObjects.climbables.push(mesh);
          break;
        case 'obstacle':
          gameObjects.hazards = gameObjects.hazards || [];
          gameObjects.hazards.push({ mesh, config: obj });
          break;
      }
    }

    return gameObjects;
  };

  const createGameObject = async (scene, obj, genre) => {
    const position = obj.position || [0, 0, 0];
    const scale = obj.scale || [1, 1, 1];
    const color = obj.color || '#06B6D4';
    let mesh;

    // Themed fallbacks carry a floating name label — synthesized stand-ins must read
    // as the intended narrative object, never as an anonymous primitive.
    const nameLabel = (target, text, tint = '#FFFFFF') => {
      try {
        const lp = BABYLON.MeshBuilder.CreatePlane(target.name + '_label', { width: 2.4, height: 0.6 }, scene);
        lp.parent = target;
        lp.position.y = 1.6;
        lp.billboardMode = BABYLON.Mesh.BILLBOARDMODE_ALL;
        const ldt = new BABYLON.DynamicTexture(target.name + '_ltx', { width: 256, height: 64 }, scene, false);
        ldt.hasAlpha = true;
        ldt.drawText(String(text).slice(0, 20), null, 44, 'bold 30px monospace', tint, 'transparent', true);
        const lm = new BABYLON.StandardMaterial(target.name + '_lm', scene);
        lm.diffuseTexture = ldt;
        lm.emissiveColor = new BABYLON.Color3(1, 1, 1);
        lm.useAlphaFromDiffuseTexture = true;
        lm.backFaceCulling = false;
        lm.disableLighting = true;
        lp.material = lm;
      } catch { /* labels are decorative */ }
    };

    const spritePlane = (sz = 2.2, yOff = 1) => {
      const m = BABYLON.MeshBuilder.CreatePlane(obj.name, { size: sz, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
      m.position = new BABYLON.Vector3(position[0], position[1] + yOff, position[2]);
      const sm = new BABYLON.StandardMaterial(obj.name + '_mat', scene);
      const applyTx = (url) => {
        const tx = new BABYLON.Texture(url, scene, false, true);
        tx.hasAlpha = true;
        sm.diffuseTexture = tx;
        sm.useAlphaFromDiffuseTexture = true;
      };
      applyTx(getCleanedSync(obj.asset_url) || obj.asset_url);
      // Preloader normally has the keyed sprite ready (single-pass, no flicker);
      // fall back to async hot-swap only when it isn't cached yet.
      if (!getCleanedSync(obj.asset_url)) {
        removeBackgroundIfNeeded(obj.asset_url).then((cleaned) => {
          if (cleaned && cleaned !== obj.asset_url && !m.isDisposed()) applyTx(cleaned);
        }).catch(() => {});
      }
      sm.backFaceCulling = false;
      sm.emissiveColor = new BABYLON.Color3(0.25, 0.25, 0.25);
      // Subjects are opaque by default; opacity is an optional per-character slider (never transparent by accident)
      sm.alpha = obj.opacity != null ? Math.max(0.1, Math.min(1, obj.opacity)) : 1;
      m.material = sm;
      return m;
    };

    // Procedural role art plane — actors NEVER fall back to boxes/spheres/capsules
    const proceduralPlane = (sz, yOff, role) => {
      const m = BABYLON.MeshBuilder.CreatePlane(obj.name, { size: sz, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
      m.position = new BABYLON.Vector3(position[0], position[1] + yOff, position[2]);
      const mt = new BABYLON.StandardMaterial(obj.name + '_mat', scene);
      const tx = new BABYLON.Texture(roleSpriteDataUrl(role, color), scene, false, true);
      tx.hasAlpha = true;
      mt.diffuseTexture = tx;
      mt.useAlphaFromDiffuseTexture = true;
      mt.backFaceCulling = false;
      mt.emissiveColor = new BABYLON.Color3(0.4, 0.4, 0.4);
      m.material = mt;
      return m;
    };

    switch (obj.type) {
      case 'player':
        if (obj.asset_url) {
          // Generated / cast hero art — render as an upright sprite plane
          mesh = spritePlane((scale[0] > 1 ? scale[0] : 2.2), 1);
          mesh.metadata = { ...(mesh.metadata || {}), isPlayer: true };
          break;
        }
        // No geometric fallback: art-less heroes get a drawn character sprite
        mesh = proceduralPlane((scale[0] > 1 ? scale[0] : 2.2), 1, 'player');
        mesh.metadata = { ...(mesh.metadata || {}), isPlayer: true };
        nameLabel(mesh, obj.name || 'Hero', '#67E8F9');
        break;

      case 'enemy':
        if (obj.asset_url) {
          mesh = spritePlane(obj.boss ? 4.2 : 2.0, obj.boss ? 1.6 : 0.7);
          if (obj.behavior === 'patrol') addPatrolBehavior(scene, mesh, position, obj);
          break;
        }
        // No geometric fallback: art-less enemies get a drawn creature sprite
        mesh = proceduralPlane(obj.boss ? 4.2 : 2.0, obj.boss ? 1.6 : 0.7, 'enemy');
        nameLabel(mesh, (obj.name || 'Enemy').replace(/_\d+$/, ''), '#FCA5A5');
        if (obj.behavior === 'patrol') {
          addPatrolBehavior(scene, mesh, position, obj);
        }
        break;

      case 'collectible':
        if (obj.asset_url) {
          mesh = spritePlane(1.1, 0.5);
          let ctime = Math.random() * Math.PI * 2;
          const cy = position[1] + 0.5;
          scene.registerBeforeRender(() => {
            if (mesh && !mesh.isDisposed()) { ctime += 0.03; mesh.position.y = cy + Math.sin(ctime) * 0.2; }
          });
          break;
        }
        // No geometric fallback: art-less collectibles are drawn glowing motes
        mesh = proceduralPlane(1.1, 0.5, 'collectible');

        // Floating and spinning animation
        let time = Math.random() * Math.PI * 2;
        const startY = position[1] + 0.5;
        scene.registerBeforeRender(() => {
          if (mesh && !mesh.isDisposed()) {
            time += 0.03;
            mesh.position.y = startY + Math.sin(time) * 0.2;
          }
        });

        // Add glow layer
        const gl = new BABYLON.GlowLayer("glow", scene);
        gl.intensity = 0.5;
        gl.addIncludedOnlyMesh(mesh);
        break;

      case 'platform':
      case 'floor':
        mesh = BABYLON.MeshBuilder.CreateBox(obj.name, { 
          width: scale[0], 
          height: scale[1], 
          depth: scale[2] 
        }, scene);
        mesh.position = new BABYLON.Vector3(position[0], position[1], position[2]);
        
        const platMat = new BABYLON.StandardMaterial(obj.name + "_mat", scene);
        if (obj.asset_url && obj.surface_mode === 'texture') {
          // Real game texture behavior: tiled repeats across the surface by world size,
          // wrap uses wrap addressing without repeat count, flat stretches once.
          const stx = new BABYLON.Texture(obj.asset_url, scene, false, true);
          const tmode = obj.texture_mode || (obj.texture_tile === false ? 'flat' : 'tiled');
          if (tmode !== 'flat') {
            stx.wrapU = BABYLON.Texture.WRAP_ADDRESSMODE;
            stx.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;
            if (tmode === 'tiled') {
              stx.uScale = obj.uv?.uScale || Math.max(1, Math.round(scale[0] / 2));
              stx.vScale = obj.uv?.vScale || Math.max(1, Math.round(Math.max(scale[1], scale[2]) / 2));
            }
          }
          platMat.diffuseTexture = stx;
          platMat.emissiveColor = new BABYLON.Color3(0.15, 0.15, 0.15);
        } else {
          // Themed surface — never flat gray/black blocks. Brighten near-black palette colors
          // and shade with a vertical gradient + top highlight so platforms read clearly.
          let base = BABYLON.Color3.FromHexString(color);
          const lum = 0.299 * base.r + 0.587 * base.g + 0.114 * base.b;
          if (lum < 0.26) {
            const f = 0.26 / Math.max(0.02, lum);
            base = new BABYLON.Color3(Math.min(1, base.r * f + 0.05), Math.min(1, base.g * f + 0.05), Math.min(1, base.b * f + 0.05));
          }
          const hexOf = (c3) => '#' + [c3.r, c3.g, c3.b].map(v => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0')).join('');
          const light = new BABYLON.Color3(Math.min(1, base.r * 1.5 + 0.1), Math.min(1, base.g * 1.5 + 0.1), Math.min(1, base.b * 1.5 + 0.1));
          const gdt = new BABYLON.DynamicTexture(obj.name + '_grid', { width: 256, height: 256 }, scene, false);
          const gctx = gdt.getContext();
          const grad = gctx.createLinearGradient(0, 0, 0, 256);
          grad.addColorStop(0, hexOf(light));
          grad.addColorStop(1, hexOf(base));
          gctx.fillStyle = grad;
          gctx.fillRect(0, 0, 256, 256);
          gctx.fillStyle = 'rgba(255,255,255,0.30)';
          gctx.fillRect(0, 0, 256, 10);
          gctx.strokeStyle = 'rgba(255,255,255,0.08)';
          gctx.lineWidth = 1;
          for (let gl = 0; gl <= 256; gl += 32) {
            gctx.beginPath(); gctx.moveTo(gl, 0); gctx.lineTo(gl, 256); gctx.stroke();
            gctx.beginPath(); gctx.moveTo(0, gl); gctx.lineTo(256, gl); gctx.stroke();
          }
          gctx.strokeStyle = 'rgba(255,255,255,0.16)';
          gctx.strokeRect(0, 0, 256, 256);
          gdt.update();
          gdt.wrapU = BABYLON.Texture.WRAP_ADDRESSMODE;
          gdt.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;
          gdt.uScale = Math.max(1, Math.round(scale[0] / 2));
          gdt.vScale = Math.max(1, Math.round(Math.max(scale[1], scale[2]) / 2));
          platMat.diffuseTexture = gdt;
          // Emissive-driven: scene lighting can never render a themed surface as black
          platMat.emissiveTexture = gdt;
          platMat.emissiveColor = new BABYLON.Color3(0.62, 0.62, 0.62);
        }
        mesh.material = platMat;
        mesh.receiveShadows = true;

        // Moving platforms — sine oscillation on the configured axis
        if (obj.moving) {
          const mv = obj.moving;
          const origin = { x: position[0], y: position[1] };
          let t = 0;
          scene.registerBeforeRender(() => {
            if (!mesh || mesh.isDisposed()) return;
            t += scene.getEngine().getDeltaTime() / 1000;
            const off = Math.sin(t * (mv.speed || 1)) * (mv.range || 3);
            if (mv.axis === 'y') mesh.position.y = origin.y + off;
            else mesh.position.x = origin.x + off;
          });
        }
        
        // Static physics
        if (scene.metadata?.physicsEnabled) {

          mesh.physicsImpostor = new BABYLON.PhysicsImpostor(
          mesh, 
          BABYLON.PhysicsImpostor.BoxImpostor, 
          { mass: 0, friction: 0.8, restitution: 0.1 }, 
          scene
        );

        }
        break;

      case 'wall':
        mesh = BABYLON.MeshBuilder.CreateBox(obj.name, { 
          width: scale[0], 
          height: scale[1] || 3, 
          depth: scale[2] 
        }, scene);
        mesh.position = new BABYLON.Vector3(position[0], position[1] + (scale[1] || 3) / 2, position[2]);
        
        const wallMat = new BABYLON.StandardMaterial(obj.name + "_mat", scene);
        {
          // Same brightness guarantee as platforms — walls never render as black slabs
          let wcol = BABYLON.Color3.FromHexString(color);
          const wlum = 0.299 * wcol.r + 0.587 * wcol.g + 0.114 * wcol.b;
          if (wlum < 0.24) {
            const wf = 0.24 / Math.max(0.02, wlum);
            wcol = new BABYLON.Color3(Math.min(1, wcol.r * wf + 0.05), Math.min(1, wcol.g * wf + 0.05), Math.min(1, wcol.b * wf + 0.05));
          }
          wallMat.diffuseColor = wcol;
          wallMat.emissiveColor = wcol.scale(0.25);
        }
        mesh.material = wallMat;
        
        if (scene.metadata?.physicsEnabled) {

        
          mesh.physicsImpostor = new BABYLON.PhysicsImpostor(
          mesh, 
          BABYLON.PhysicsImpostor.BoxImpostor, 
          { mass: 0, friction: 0.5 }, 
          scene
        );

        
        }
        break;

      case 'goal':
        if (obj.asset_url) {
          mesh = spritePlane(2.8, 1.4);
          break;
        }
        // No geometric fallback: art-less goals are drawn beacon archways
        mesh = proceduralPlane(2.8, 1.4, 'goal');
        nameLabel(mesh, obj.name || 'Exit', '#6EE7B7');
        break;

      case 'obstacle': {
        // NOTHING hardcoded: hazards render ONLY with their own saved art.
        // Asset-less obstacles (e.g. AI leftovers) are skipped entirely — no
        // procedural spike-star placeholder ever appears in a game.
        if (obj.asset_url) {
          mesh = spritePlane(1.3, 0.55);
        } else {
          return;
        }
        break;
      }

      case 'climbable':
        mesh = BABYLON.MeshBuilder.CreateBox(obj.name, { width: scale[0] || 0.6, height: scale[1] || 4, depth: scale[2] || 0.6 }, scene);
        mesh.position = new BABYLON.Vector3(position[0], position[1], position[2]);
        {
          // Drawn ladder/vine surface — never a bare colored block
          const clMat = new BABYLON.StandardMaterial(obj.name + "_mat", scene);
          const ldt = new BABYLON.DynamicTexture(obj.name + '_ladder', { width: 128, height: 256 }, scene, false);
          const lctx = ldt.getContext();
          const clBase = color || '#16A34A';
          lctx.clearRect(0, 0, 128, 256);
          lctx.strokeStyle = clBase;
          lctx.lineWidth = 14;
          lctx.beginPath(); lctx.moveTo(28, 0); lctx.lineTo(28, 256); lctx.stroke(); // left rail
          lctx.beginPath(); lctx.moveTo(100, 0); lctx.lineTo(100, 256); lctx.stroke(); // right rail
          lctx.lineWidth = 9;
          lctx.strokeStyle = 'rgba(255,255,255,0.55)';
          for (let ry = 20; ry < 256; ry += 36) { lctx.beginPath(); lctx.moveTo(28, ry); lctx.lineTo(100, ry); lctx.stroke(); } // rungs
          ldt.update();
          ldt.hasAlpha = true;
          ldt.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;
          ldt.vScale = Math.max(1, Math.round((scale[1] || 4) / 2));
          clMat.diffuseTexture = ldt;
          clMat.useAlphaFromDiffuseTexture = true;
          clMat.backFaceCulling = false;
          clMat.emissiveColor = new BABYLON.Color3(0.15, 0.3, 0.15);
          mesh.material = clMat;
        }
        break;

      case 'decoration':
        if (obj.asset_url) {
          mesh = spritePlane(scale[0] || 1.6, 0.6);
          break;
        }
        // No geometric fallback: art-less decor is a drawn foliage sprite
        mesh = proceduralPlane(scale[0] || 1.6, 0.6, 'decoration');
        break;

      // Explicit user-selected primitives — the ONLY path that renders raw geometric shapes
      case 'cube':
      case 'cylinder':
      case 'sphere':
      case 'cone':
      case 'torus':
      case 'ring':
      case 'capsule':
      case 'plane': {
        if (obj.type === 'cube') mesh = BABYLON.MeshBuilder.CreateBox(obj.name, { width: scale[0], height: scale[1], depth: scale[2] }, scene);
        else if (obj.type === 'cylinder') mesh = BABYLON.MeshBuilder.CreateCylinder(obj.name, { height: scale[1] || 1, diameter: scale[0] || 1 }, scene);
        else if (obj.type === 'cone') mesh = BABYLON.MeshBuilder.CreateCylinder(obj.name, { height: scale[1] || 1, diameterTop: 0, diameterBottom: scale[0] || 1 }, scene);
        else if (obj.type === 'torus' || obj.type === 'ring') mesh = BABYLON.MeshBuilder.CreateTorus(obj.name, { diameter: scale[0] || 1, thickness: (scale[0] || 1) * 0.3 }, scene);
        else if (obj.type === 'capsule') mesh = BABYLON.MeshBuilder.CreateCapsule(obj.name, { height: scale[1] || 1, radius: (scale[0] || 1) * 0.25 }, scene);
        else if (obj.type === 'plane') mesh = BABYLON.MeshBuilder.CreatePlane(obj.name, { size: scale[0] || 1, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
        else mesh = BABYLON.MeshBuilder.CreateSphere(obj.name, { diameter: scale[0] || 1 }, scene);
        mesh.position = new BABYLON.Vector3(position[0], position[1], position[2]);
        const prMat = new BABYLON.StandardMaterial(obj.name + "_mat", scene);
        prMat.diffuseColor = BABYLON.Color3.FromHexString(color);
        mesh.material = prMat;
        break;
      }

      case 'sprite': {
        // Textured billboard sprite from user-imported asset
        const spriteSize = scale[0] || 2.5;
        mesh = BABYLON.MeshBuilder.CreatePlane(obj.name, { size: spriteSize, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
        mesh.position = new BABYLON.Vector3(position[0], position[1], position[2]);
        // Only opt in to billboarding when the object explicitly requests it — otherwise
        // designers lose the ability to rotate the sprite.
        if (obj.billboard === true) mesh.billboardMode = BABYLON.Mesh.BILLBOARDMODE_Y;
        const smat = new BABYLON.StandardMaterial(obj.name + "_mat", scene);
        if (obj.asset_url) {
          // invertY=true so PNG/JPG sprites don't render upside-down.
          const tx = new BABYLON.Texture(obj.asset_url, scene, false, true);
          tx.hasAlpha = true;
          smat.diffuseTexture = tx;
          smat.useAlphaFromDiffuseTexture = true;
        } else {
          smat.diffuseColor = BABYLON.Color3.FromHexString(color);
        }
        smat.emissiveColor = new BABYLON.Color3(0.15, 0.15, 0.15);
        smat.backFaceCulling = false;
        mesh.material = smat;
        break;
      }

      case 'texture_prop': {
        // Textured cube (uses asset_url as diffuse on all faces) — RGBA preserved so
        // transparent art is never trapped inside an opaque rectangular frame.
        const boxSize = scale[0] || 2;
        mesh = BABYLON.MeshBuilder.CreateBox(obj.name, { size: boxSize }, scene);
        mesh.position = new BABYLON.Vector3(position[0], position[1], position[2]);
        const bmat = new BABYLON.StandardMaterial(obj.name + "_mat", scene);
        if (obj.asset_url) {
          const tx = new BABYLON.Texture(obj.asset_url, scene, false, true);
          tx.hasAlpha = true;
          bmat.diffuseTexture = tx;
          bmat.useAlphaFromDiffuseTexture = true;
          bmat.backFaceCulling = false;
        } else {
          bmat.diffuseColor = BABYLON.Color3.FromHexString(color);
        }
        mesh.material = bmat;
        break;
      }

      case 'background': {
        // Wide plane behind everything, locked behind camera. Textured with alpha-friendly material.
        const w = (scale[0] || 10) * 2;
        const h = scale[1] || 5;
        mesh = BABYLON.MeshBuilder.CreatePlane(obj.name, { width: w, height: h, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
        mesh.position = new BABYLON.Vector3(position[0], position[1], position[2] + 15);
        mesh.renderingGroupId = 0;
        mesh.isPickable = false;
        const bgMat = new BABYLON.StandardMaterial(obj.name + "_mat", scene);
        if (obj.asset_url) {
          const tx = new BABYLON.Texture(obj.asset_url, scene, false, true);
          if (obj.texture_mode === 'tiled') {
            tx.wrapU = BABYLON.Texture.WRAP_ADDRESSMODE;
            tx.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;
            tx.uScale = obj.uv?.uScale || 4;
            tx.vScale = obj.uv?.vScale || 2;
          }
          bgMat.diffuseTexture = tx;
          bgMat.emissiveTexture = tx;
        } else {
          // Procedural painted backdrop — gradient sky, glow motes and hill silhouettes
          const bdt = new BABYLON.DynamicTexture(obj.name + '_bg', { width: 1024, height: 512 }, scene, false);
          const bctx = bdt.getContext();
          const bgBase = color && color.startsWith('#') ? color : '#0EA5E9';
          const bg3 = BABYLON.Color3.FromHexString(bgBase);
          const hx = (c3, f, add = 0) => '#' + [c3.r, c3.g, c3.b].map(v => Math.round(Math.min(1, Math.max(0, v * f + add)) * 255).toString(16).padStart(2, '0')).join('');
          const grad = bctx.createLinearGradient(0, 0, 0, 512);
          grad.addColorStop(0, hx(bg3, 0.35));
          grad.addColorStop(0.6, hx(bg3, 0.9, 0.04));
          grad.addColorStop(1, hx(bg3, 1.3, 0.1));
          bctx.fillStyle = grad;
          bctx.fillRect(0, 0, 1024, 512);
          for (let i = 0; i < 40; i++) { // glow motes
            const mx = Math.random() * 1024, my = Math.random() * 360, mr = 1 + Math.random() * 2.5;
            bctx.fillStyle = `rgba(255,255,255,${0.15 + Math.random() * 0.35})`;
            bctx.beginPath(); bctx.arc(mx, my, mr, 0, Math.PI * 2); bctx.fill();
          }
          bctx.fillStyle = hx(bg3, 0.45); // far hills
          bctx.beginPath(); bctx.moveTo(0, 512);
          for (let bxp = 0; bxp <= 1024; bxp += 64) bctx.lineTo(bxp, 400 - Math.abs(Math.sin(bxp * 0.011)) * 90);
          bctx.lineTo(1024, 512); bctx.closePath(); bctx.fill();
          bctx.fillStyle = hx(bg3, 0.25); // near hills
          bctx.beginPath(); bctx.moveTo(0, 512);
          for (let bxp = 0; bxp <= 1024; bxp += 48) bctx.lineTo(bxp, 460 - Math.abs(Math.cos(bxp * 0.017)) * 70);
          bctx.lineTo(1024, 512); bctx.closePath(); bctx.fill();
          bdt.update();
          bgMat.diffuseTexture = bdt;
          bgMat.emissiveTexture = bdt;
        }
        bgMat.backFaceCulling = false;
        mesh.material = bgMat;
        // If parallax flag set, tag mesh so a future update loop can offset it with camera motion.
        if (obj.parallax) mesh.metadata = { ...(mesh.metadata || {}), parallax: true };
        break;
      }

      case 'model_3d': {
        // Async .glb / .gltf load. Placeholder invisible cube while loading so game keeps playing.
        mesh = BABYLON.MeshBuilder.CreateBox(obj.name + '_placeholder', { size: 0.01 }, scene);
        mesh.position = new BABYLON.Vector3(position[0], position[1], position[2]);
        mesh.isVisible = false;
        if (obj.asset_url && /\.(glb|gltf)(\?|$)/i.test(obj.asset_url)) {
          try {
            BABYLON.SceneLoader.ImportMesh('', '', obj.asset_url, scene, (loaded) => {
              if (loaded[0]) {
                loaded[0].name = obj.name;
                loaded[0].position = new BABYLON.Vector3(position[0], position[1], position[2]);
                loaded[0].checkCollisions = true;
              }
            });
          } catch { /* placeholder remains */ }
        }
        break;
      }

      case 'particle': {
        // Babylon.js GPU/CPU particle system — spawns from an emitter mesh at the object's position.
        const emitter = BABYLON.MeshBuilder.CreateSphere(obj.name, { diameter: 0.2 }, scene);
        emitter.position = new BABYLON.Vector3(position[0], position[1], position[2]);
        emitter.isVisible = false;
        try {
          const ps = new BABYLON.ParticleSystem(obj.name + '_ps', obj.particles?.capacity || 500, scene);
          ps.particleTexture = new BABYLON.Texture(obj.asset_url || 'https://assets.babylonjs.com/textures/flare.png', scene);
          ps.emitter = emitter;
          ps.minEmitBox = new BABYLON.Vector3(-0.1, 0, -0.1);
          ps.maxEmitBox = new BABYLON.Vector3(0.1, 0, 0.1);
          ps.color1 = BABYLON.Color4.FromHexString(obj.particles?.color1 || (color + 'FF'));
          ps.color2 = BABYLON.Color4.FromHexString(obj.particles?.color2 || '#FFFFFFFF');
          ps.colorDead = new BABYLON.Color4(0, 0, 0, 0);
          ps.minSize = obj.particles?.min_size || 0.1;
          ps.maxSize = obj.particles?.max_size || 0.5;
          ps.minLifeTime = obj.particles?.min_lifetime || 0.3;
          ps.maxLifeTime = obj.particles?.max_lifetime || 1.5;
          ps.emitRate = obj.particles?.emit_rate || 100;
          ps.direction1 = new BABYLON.Vector3(-1, 2, -1);
          ps.direction2 = new BABYLON.Vector3(1, 4, 1);
          ps.gravity = new BABYLON.Vector3(0, -3, 0);
          ps.blendMode = BABYLON.ParticleSystem.BLENDMODE_ONEONE;
          ps.start();
        } catch { /* particle setup failed, keep emitter marker */ }
        mesh = emitter;
        break;
      }

      case 'light':
        const light = new BABYLON.PointLight(obj.name, new BABYLON.Vector3(position[0], position[1], position[2]), scene);
        light.intensity = obj.intensity || 1;
        light.diffuse = BABYLON.Color3.FromHexString(color);
        
        // Add visible light sphere
        const lightSphere = BABYLON.MeshBuilder.CreateSphere(obj.name + "_vis", { diameter: 0.3 }, scene);
        lightSphere.position = new BABYLON.Vector3(position[0], position[1], position[2]);
        const lightMat = new BABYLON.StandardMaterial(obj.name + "_mat", scene);
        lightMat.emissiveColor = BABYLON.Color3.FromHexString(color);
        lightSphere.material = lightMat;
        return null;

      default:
        // Zero unsolicited primitives: unknown entity roles never fall back to a cube.
        if (obj.asset_url) {
          // Unknown role with art → alpha-preserving billboard sprite
          mesh = BABYLON.MeshBuilder.CreatePlane(obj.name, { size: scale[0] || 2, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
          mesh.position = new BABYLON.Vector3(position[0], position[1], position[2]);
          const defMat = new BABYLON.StandardMaterial(obj.name + "_mat", scene);
          const tx = new BABYLON.Texture(obj.asset_url, scene, false, true);
          tx.hasAlpha = true;
          defMat.diffuseTexture = tx;
          defMat.useAlphaFromDiffuseTexture = true;
          defMat.backFaceCulling = false;
          mesh.material = defMat;
        } else {
          console.warn(`PlayMold: skipped unknown entity type "${obj.type}" (${obj.name}) — no primitive fallback`);
          return null;
        }
    }

    if (mesh) {
      mesh.checkCollisions = true;
      // WYSIWYG parity with the editor: apply saved rotation (players excluded — the
      // runtime steers their facing) and absolute gizmo-scale normalization.
      if (obj.rotation && !mesh.metadata?.isPlayer) {
        mesh.rotation.x = obj.rotation[0] || 0;
        mesh.rotation.y = obj.rotation[1] || 0;
        mesh.rotation.z = obj.rotation[2] || 0;
      }
      if (obj.type === 'npc') mesh.metadata = { ...(mesh.metadata || {}), coopEligible: true };
      if (obj.scale_abs && obj.scale) {
        // Editor stores absolute world extents; normalize whatever geometry this type baked.
        try {
          const ext = mesh.getBoundingInfo()?.boundingBox?.extendSize;
          if (ext) {
            const bw = ext.x * 2 || 1, bh = ext.y * 2 || 1, bd = ext.z * 2;
            mesh.scaling.x = obj.scale[0] / bw;
            mesh.scaling.y = (obj.scale[1] ?? obj.scale[0]) / bh;
            if (bd > 0.001) mesh.scaling.z = (obj.scale[2] ?? obj.scale[0]) / bd;
          }
        } catch { /* keep baked size */ }
      }
      // If the object declares advanced texture maps (normal / AO / emissive / metallic / roughness / PBR
      // or UV tiling / alpha_mode), upgrade the material with the full pipeline.
      const hasAdvanced = !!(obj.textures || obj.normal_url || obj.ao_url || obj.emissive_url ||
                              obj.metallic_url || obj.roughness_url ||
                              (obj.uv && (obj.uv.uScale !== undefined || obj.uv.vScale !== undefined)) ||
                              (obj.material && (obj.material.pbr || obj.material.alpha_mode)));
      if (hasAdvanced) {
        try {
          mesh.material = buildObjectMaterial(BABYLON, scene, obj, color);
        } catch (e) { /* keep the basic material on failure */ }
      }
    }

    return mesh;
  };

  const createCharacterMesh = (scene, name, type) => {
    // Create a simple but recognizable character
    const body = BABYLON.MeshBuilder.CreateCapsule(name + "_body", { height: 1.5, radius: 0.4 }, scene);
    const head = BABYLON.MeshBuilder.CreateSphere(name + "_head", { diameter: 0.6 }, scene);
    head.position.y = 1;
    head.parent = body;

    // Eyes
    const eyeL = BABYLON.MeshBuilder.CreateSphere(name + "_eyeL", { diameter: 0.15 }, scene);
    eyeL.position = new BABYLON.Vector3(-0.15, 1.1, 0.25);
    eyeL.parent = body;
    const eyeR = BABYLON.MeshBuilder.CreateSphere(name + "_eyeR", { diameter: 0.15 }, scene);
    eyeR.position = new BABYLON.Vector3(0.15, 1.1, 0.25);
    eyeR.parent = body;

    const eyeMat = new BABYLON.StandardMaterial(name + "_eyeMat", scene);
    eyeMat.diffuseColor = new BABYLON.Color3(1, 1, 1);
    eyeL.material = eyeMat;
    eyeR.material = eyeMat;

    // Material based on type
    const mat = new BABYLON.StandardMaterial(name + "_mat", scene);
    if (type === 'player') {
      mat.diffuseColor = new BABYLON.Color3(0, 0.7, 0.9);
      mat.emissiveColor = new BABYLON.Color3(0, 0.1, 0.2);
      // Tag the body mesh so findPlayerMesh() picks it up for compiled script ctx.player.
      body.metadata = { ...(body.metadata || {}), isPlayer: true };
    } else {
      mat.diffuseColor = new BABYLON.Color3(0.9, 0.2, 0.2);
      mat.emissiveColor = new BABYLON.Color3(0.2, 0, 0);
    }
    body.material = mat;
    head.material = mat;

    return body;
  };

  // ---- Deterministic fixed-timestep scheduler (Unity FixedUpdate / Godot _physics_process) ----
  // Gameplay-affecting movement steps at a constant 60Hz regardless of monitor refresh rate.
  // Real time accumulates; 0-N catch-up steps per frame, capped to avoid spiral-of-death.
  const FIXED_DT = 1 / 60;
  const getFixedStepper = (scene) => {
    if (fixedSteppers.has(scene)) return fixedSteppers.get(scene);
    const callbacks = new Set();
    let accum = 0;
    scene.registerBeforeRender(() => {
      if (startGateRef.current) { accum = 0; return; } // game waits until the player confirms the briefing
      accum += Math.min(0.25, scene.getEngine().getDeltaTime() / 1000);
      let n = 0;
      while (accum >= FIXED_DT && n < 5) {
        for (const cb of Array.from(callbacks)) { try { cb(FIXED_DT); } catch { /* a bad step never crashes the loop */ } }
        accum -= FIXED_DT; n++;
      }
      if (n === 5) accum = 0;
    });
    const stepper = { add: (cb) => callbacks.add(cb), remove: (cb) => callbacks.delete(cb) };
    fixedSteppers.set(scene, stepper);
    return stepper;
  };

  // ---- Combat impact FX: every hit bursts + every gain/loss pops on screen ----
  const spawnBurst = (scene, pos, colorHex = '#FCD34D', count = 26, size = 0.35) => {
    try {
      const ps = new BABYLON.ParticleSystem('hitfx', count, scene);
      ps.particleTexture = null;
      ps.emitter = pos.clone();
      const c = BABYLON.Color3.FromHexString(colorHex);
      ps.color1 = new BABYLON.Color4(c.r, c.g, c.b, 1);
      ps.color2 = new BABYLON.Color4(Math.min(1, c.r * 1.4 + 0.2), Math.min(1, c.g * 1.4 + 0.2), Math.min(1, c.b * 1.4 + 0.2), 1);
      ps.colorDead = new BABYLON.Color4(c.r, c.g, c.b, 0);
      ps.minSize = size * 0.4; ps.maxSize = size;
      ps.minLifeTime = 0.15; ps.maxLifeTime = 0.5;
      ps.emitRate = 0;
      ps.manualEmitCount = count;
      ps.minEmitPower = 3; ps.maxEmitPower = 7;
      ps.direction1 = new BABYLON.Vector3(-1, -0.6, -1);
      ps.direction2 = new BABYLON.Vector3(1, 1.4, 1);
      ps.gravity = new BABYLON.Vector3(0, -6, 0);
      ps.blendMode = BABYLON.ParticleSystem.BLENDMODE_ADD;
      ps.disposeOnStop = true;
      ps.start();
      setTimeout(() => { try { ps.stop(); } catch { /* gone */ } }, 120);
    } catch { /* FX are decorative */ }
  };

  const scorePop = (scene, pos, text, colorHex = '#FCD34D') => {
    try {
      const plane = BABYLON.MeshBuilder.CreatePlane(`pop_${Date.now()}`, { width: 2.4, height: 0.9 }, scene);
      plane.position = pos.clone(); plane.position.y += 1.2;
      plane.billboardMode = BABYLON.Mesh.BILLBOARDMODE_ALL;
      const dt = new BABYLON.DynamicTexture(`popt_${Date.now()}`, { width: 256, height: 96 }, scene, false);
      dt.hasAlpha = true;
      const ctx = dt.getContext();
      ctx.clearRect(0, 0, 256, 96);
      ctx.font = 'bold 52px Arial';
      ctx.textAlign = 'center';
      ctx.strokeStyle = 'rgba(0,0,0,0.85)'; ctx.lineWidth = 8;
      ctx.strokeText(text, 128, 64);
      ctx.fillStyle = colorHex;
      ctx.fillText(text, 128, 64);
      dt.update();
      const m = new BABYLON.StandardMaterial(`popm_${Date.now()}`, scene);
      m.diffuseTexture = dt; m.emissiveTexture = dt; m.useAlphaFromDiffuseTexture = true;
      m.backFaceCulling = false; m.disableLighting = true;
      plane.material = m;
      let t = 0;
      const rise = () => {
        if (plane.isDisposed()) return;
        t += scene.getEngine().getDeltaTime() / 1000;
        plane.position.y += 0.02;
        m.alpha = Math.max(0, 1 - t / 0.9);
        if (t > 0.9) { scene.unregisterBeforeRender(rise); plane.dispose(false, true); }
      };
      scene.registerBeforeRender(rise);
    } catch { /* FX are decorative */ }
  };

  const addPatrolBehavior = (scene, mesh, startPos, config = {}) => {
    let direction = 1;
    // Honor `patrol_dist` from template metadata (fallback 5 units)
    const patrolRange = (config && (config.patrol_dist || config.patrol_range)) || 5;
    const speed = (config && config.patrol_speed) || 0.03;
    const startX = startPos[0];
    const perSec = speed * 60; // legacy per-frame speed was calibrated at 60fps

    getFixedStepper(scene).add((dt) => {
      if (mesh && !mesh.isDisposed()) {
        mesh.position.x += perSec * direction * dt;
        if (mesh.position.x > startX + patrolRange) direction = -1;
        if (mesh.position.x < startX - patrolRange) direction = 1;
        mesh.rotation.y = direction > 0 ? 0 : Math.PI;
      }
    });
  };

  const setupPlayerControls = (scene, player, engine, settings, gameObjects = {}) => {
    const inputMap = inputMapRef.current;
    Object.keys(inputMap).forEach((k) => delete inputMap[k]);
    const speed = settings?.player_speed || 5;
    const jumpForce = settings?.jump_force || 8;
    let canJump = true;
    let isGrounded = true;
    // Manual jump/gravity state — used when no physics plugin is loaded
    let verticalVel = 0;
    const manualGravity = -20;      // m/s^2
    const manualJumpVel = jumpForce; // initial upward velocity
    // Capture the player's configured spawn Y as the ground baseline so
    // the manual-gravity fallback doesn't teleport the player to y=1.
    const groundY = player.position.y;

    // Keyboard input — dual layer: BABYLON ActionManager (canvas-focused) + window (fallback)
    scene.actionManager = new BABYLON.ActionManager(scene);
    
    scene.actionManager.registerAction(new BABYLON.ExecuteCodeAction(
      BABYLON.ActionManager.OnKeyDownTrigger,
      (evt) => { inputMap[evt.sourceEvent.key.toLowerCase()] = true; }
    ));
    
    scene.actionManager.registerAction(new BABYLON.ExecuteCodeAction(
      BABYLON.ActionManager.OnKeyUpTrigger,
      (evt) => { inputMap[evt.sourceEvent.key.toLowerCase()] = false; }
    ));

    // Window-level fallback — works even when canvas doesn't have focus.
    // Prevents default on arrows/space to stop page from scrolling.
    const onKeyDown = (e) => {
      const k = e.key.toLowerCase();
      inputMap[k] = true;
      if (['arrowup','arrowdown','arrowleft','arrowright',' '].includes(k)) e.preventDefault();
    };
    const onKeyUp = (e) => { inputMap[e.key.toLowerCase()] = false; };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    scene.onDisposeObservable.add(() => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    });

    // Auto-focus the canvas so first click isn't required
    try { canvasRef.current?.focus?.(); canvasRef.current?.setAttribute('tabindex', '0'); } catch { /* ignore */ }

    // Movement with physics — runs on the fixed-timestep scheduler so gameplay speed is
    // identical on a 60Hz laptop and a 144Hz desktop; delta is always exactly FIXED_DT.
    let jumpCooldown = 0;
    const physicsStep = (delta) => {
      if (!player || player.isDisposed()) return;
      if (jumpCooldown > 0) { jumpCooldown -= delta; if (jumpCooldown <= 0) canJump = true; }

      const velocity = new BABYLON.Vector3(0, 0, 0);

      // Get camera forward direction for relative movement
      const camera = scene.activeCamera;
      let forward = camera.getTarget().subtract(camera.position);
      forward.y = 0;
      forward.normalize();
      
      // Right vector — Babylon left-handed: right = up × forward (not forward × up)
      let right = BABYLON.Vector3.Cross(BABYLON.Vector3.Up(), forward);
      right.normalize();

      if (inputMap['w'] || inputMap['arrowup']) {
        velocity.addInPlace(forward.scale(speed * delta));
      }
      if (inputMap['s'] || inputMap['arrowdown']) {
        velocity.addInPlace(forward.scale(-speed * delta));
      }
      if (inputMap['a'] || inputMap['arrowleft']) {
        velocity.addInPlace(right.scale(-speed * delta));
      }
      if (inputMap['d'] || inputMap['arrowright']) {
        velocity.addInPlace(right.scale(speed * delta));
      }

      // Apply movement
      player.position.addInPlace(velocity);

      // Climbable zones — near a vine/ladder, hold Up to climb; gravity suspended
      const climbs = gameObjects.climbables || [];
      let onClimb = false;
      for (const cm of climbs) {
        if (cm && !cm.isDisposed() && BABYLON.Vector3.Distance(player.position, cm.position) < 1.8) { onClimb = true; break; }
      }
      if (onClimb) {
        if (inputMap['w'] || inputMap['arrowup']) player.position.y += speed * 0.7 * delta;
        verticalVel = 0;
        isGrounded = true;
      }

      // Jumping
      if ((inputMap[' '] || inputMap['space']) && canJump && isGrounded && !onClimb) {
        if (player.physicsImpostor) {
          player.physicsImpostor.applyImpulse(
            new BABYLON.Vector3(0, jumpForce, 0),
            player.getAbsolutePosition()
          );
        } else {
          // Manual jump fallback (no physics plugin) — kicks off vertical velocity
          verticalVel = manualJumpVel;
        }
        canJump = false;
        isGrounded = false;
        jumpCooldown = 0.3;
      }

      // Manual gravity — only when no physics impostor. Keeps player above ground.
      if (!player.physicsImpostor && !onClimb) {
        verticalVel += manualGravity * delta;
        player.position.y += verticalVel * delta;
        // Ground clamp — use the captured spawn baseline (not hardcoded).
        if (player.position.y <= groundY) {
          player.position.y = groundY;
          verticalVel = 0;
          isGrounded = true;
        } else {
          isGrounded = false;
        }
      } else {
        // Ground check via ray only when physics is on
        const ray = new BABYLON.Ray(player.position, new BABYLON.Vector3(0, -1, 0), 1.2);
        const hit = scene.pickWithRay(ray, (mesh) => mesh !== player && mesh.isPickable);
        isGrounded = hit && hit.hit;
      }

      // Rotate player to face movement direction
      if (velocity.length() > 0.01) {
        const angle = Math.atan2(velocity.x, velocity.z);
        player.rotation.y = angle;
      }
    };
    getFixedStepper(scene).add(physicsStep);
  };

  const setupCollisionDetection = (scene, player, gameObjects) => {
    scene.registerBeforeRender(() => {
      if (!player || player.isDisposed()) return;

      // Check collectible collisions — the hero AND any co-op players can collect
      gameObjects.collectibles.forEach((item, index) => {
        if (item.collected || !item.mesh) return;
        
        const collectors = [player, ...coopRef.current.map(u => u.mesh)].filter(mm => mm && !mm.isDisposed());
        const distance = Math.min(...collectors.map(mm => BABYLON.Vector3.Distance(mm.position, item.mesh.position)));
        if (distance < 1.5) {
          // Collect item
          item.collected = true;
          item.mesh.dispose();
          const pickup = item.config?.pickup;
          if (pickup === 'weapon') {
            damageBuffRef.current = { until: performance.now() + 20000, multiplier: 2 };
            toast.success('⚔ Weapon acquired — double damage for 20s!');
          }
          addScoreStageAware(pickup === 'trophy' ? 50 : 10);
          setCollectiblesCount(prev => prev + 1);
        }
      });

      // Check enemy collisions — contact damages BOTH sides; enemies with 0 HP drop loot
      const now = performance.now();
      // Fair-spawn pass (once): enemies that spawned on top of the player are pushed
      // out to a fair engagement distance — no unavoidable spawn kills, ever.
      if (!gameObjects._fairSpawned) {
        gameObjects._fairSpawned = true;
        gameObjects.enemies.forEach((enemy) => {
          if (!enemy.mesh || enemy.mesh.isDisposed() || enemy.config?.friendly) return;
          const d = BABYLON.Vector3.Distance(player.position, enemy.mesh.position);
          if (d < 7) {
            let dir = enemy.mesh.position.subtract(player.position);
            dir.y = 0;
            if (dir.lengthSquared() < 0.01) dir = new BABYLON.Vector3(1, 0, 0.5);
            dir.normalize();
            enemy.mesh.position.x = player.position.x + dir.x * 9;
            enemy.mesh.position.z = player.position.z + dir.z * 9;
          }
        });
      }
      gameObjects.enemies.forEach((enemy) => {
        if (!enemy.mesh || enemy.mesh.isDisposed()) return;
        if (enemy._hp === undefined) enemy._hp = enemy.config?.hp || 20;

        const distance = BABYLON.Vector3.Distance(player.position, enemy.mesh.position);
        if (distance < 1.5 && !enemy.config?.friendly && (enemy.config?.damage ?? 10) > 0 && !playerInvulnerable()) {
          if (!enemy._lastHit || now - enemy._lastHit > 600) {
            enemy._lastHit = now;
            const dmg = (enemy.config && enemy.config.damage) || 10;
            spawnBurst(scene, player.position, '#EF4444', 20, 0.35);
            scorePop(scene, player.position, `-${dmg} HP`, '#F87171');
            setHealth(prev => {
              const newHealth = prev - dmg;
              if (newHealth <= 0) setGameOver(true);
              return Math.max(0, newHealth);
            });
            // Player contact deals damage back to the enemy — 2× while buffed
            const buff = damageBuffRef.current && performance.now() < damageBuffRef.current.until ? damageBuffRef.current.multiplier : 1;
            enemy._hp -= 8 * buff;
            playEnemyCue('hit');
            spawnBurst(scene, enemy.mesh.position, '#FCD34D', 14, 0.28);
            if (enemy._hp <= 0) {
              // Death + loot drop — contact kills reward score too (combo multiplied)
              const dropPos = enemy.mesh.position.clone();
              enemy.mesh.dispose();
              playEnemyCue('death');
              const mult = registerKill();
              const gain = (enemy.config?.boss ? 150 : 25) * mult;
              addScoreStageAware(gain);
              spawnBurst(scene, dropPos, '#F97316', 34, 0.5);
              scorePop(scene, dropPos, mult > 1 ? `+${gain} ×${mult}` : `+${gain}`, mult > 1 ? '#FB923C' : '#FCD34D');
              spawnLoot(scene, dropPos, gameObjects);
            }
          }
        }
      });

      // Loot pickups
      (gameObjects.loot || []).forEach((l) => {
        if (!l.mesh || l.mesh.isDisposed()) return;
        // Gentle bob + spin so the pickup reads clearly
        l.mesh.rotation.y += 0.04;
        l.mesh.position.y = l.baseY + Math.sin(now / 300 + l.phase) * 0.15;
        if (BABYLON.Vector3.Distance(player.position, l.mesh.position) < 1.3) {
          if (l.kind === 'health') {
            setHealth(prev => Math.min(startingHealthRef.current || 100, prev + 25));
            playEnemyCue('heal');
          } else if (l.kind === 'gem') {
            // Temporary damage buff — 2× player→enemy damage for 10s
            damageBuffRef.current = { multiplier: 2, until: performance.now() + 10000 };
            setBuffActive(true);
            setTimeout(() => {
              if (performance.now() >= damageBuffRef.current.until) {
                damageBuffRef.current = { multiplier: 1, until: 0 };
                setBuffActive(false);
              }
            }, 10050);
            playEnemyCue('gem');
          } else {
            setCollectiblesCount(prev => prev + 1);
            playEnemyCue('coin');
          }
          l.mesh.dispose();
        }
      });

      // Pressure plates -> open matching doors
      if (gameObjects.plates && gameObjects.doors) {
        gameObjects.plates.forEach((p) => {
          if (!p.mesh || p.mesh.isDisposed()) return;
          const d = BABYLON.Vector3.Distance(player.position, p.mesh.position);
          const on = d < 1.5;
          if (on !== p.activated) {
            p.activated = on;
            // Visually depress the plate
            p.mesh.position.y = (p.config.position || [0, 0.05, 0])[1] + (on ? -0.05 : 0);
          }
        });
        const anyOn = gameObjects.plates.some(p => p.activated);
        gameObjects.doors.forEach((door) => {
          if (!door.mesh || door.mesh.isDisposed()) return;
          if (anyOn && !door.open) {
            door.open = true;
            door.mesh.position.y = door.initialY + 4; // slide up
            door.mesh.checkCollisions = false;
          } else if (!anyOn && door.open) {
            door.open = false;
            door.mesh.position.y = door.initialY;
            door.mesh.checkCollisions = true;
          }
        });
      }

      // Hazards damage on contact
      (gameObjects.hazards || []).forEach((hz) => {
        if (!hz.mesh || hz.mesh.isDisposed()) return;
        if (BABYLON.Vector3.Distance(player.position, hz.mesh.position) < 1.3 && !playerInvulnerable()) {
          if (!hz._lastHit || now - hz._lastHit > 900) {
            hz._lastHit = now;
            const dmg = hz.config?.damage || 15;
            playEnemyCue('hit');
            setHealth(prev => { const nv = prev - dmg; if (nv <= 0) setGameOver(true); return Math.max(0, nv); });
          }
        }
      });

      // Objective checks — destroy_all / survive
      const objective = pdRef.current?.objective || 'collect_and_reach';
      if (objective === 'destroy_all') {
        const alive = gameObjects.enemies.filter(e => e.mesh && !e.mesh.isDisposed());
        if (alive.length === 0 && gameObjects.enemies.length > 0) handleStageClear();
      } else if (objective === 'survive') {
        const surviveSecs = pdRef.current?.survive_seconds || 45;
        if ((performance.now() - surviveStartRef.current) / 1000 >= surviveSecs) handleStageClear();
      }

      // Check goal collision — objective-aware
      gameObjects.goals.forEach((goal) => {
        if (!goal || goal.isDisposed()) return;
        
        const distance = BABYLON.Vector3.Distance(player.position, goal.position);
        if (distance < 2) {
          if (objective === 'collect_and_reach') {
            const remaining = gameObjects.collectibles.filter(c => !c.collected).length;
            if (remaining > 0) {
              const nowT = performance.now();
              if (!goal._lastHint || nowT - goal._lastHint > 3000) {
                goal._lastHint = nowT;
                toast.info(`Collect ${remaining} more item${remaining > 1 ? 's' : ''} first!`);
              }
              return;
            }
          }
          handleStageClear();
        }
      });
    });
  };

  // ---- Stage progression: advance to the next stage or win the game ----
  const handleStageClear = () => {
    if (stageClearingRef.current) return;
    const pd = pdRef.current;
    const total = pd?.stages?.length || 1;
    if (stageIdxRef.current < total - 1) {
      stageClearingRef.current = true;
      telemetry('stage_cleared');
      playEnemyCue('coin');
      const clearBonus = 150 * (stageIdxRef.current + 1); // reward escalates per stage
      setScore(prev => {
        const v = prev + clearBonus;
        // New level's score baseline: win_score is measured per level, not cumulative —
        // otherwise the clear bonus overshoots the next threshold and progression stalls.
        stageScoreBaseRef.current = v;
        return v;
      });
      setStageBanner(`Stage ${stageIdxRef.current + 1} Cleared!  +${clearBonus} Bonus`);
      setTimeout(() => {
        stageIdxRef.current += 1;
        setStageIdx(stageIdxRef.current);
        surviveStartRef.current = performance.now();
        setStageBanner(null);
        stageClearingRef.current = false;
        if (musicStopRef.current) { musicStopRef.current(); musicStopRef.current = null; }
        stopWeatherAmbience();
        if (engineRef.current) engineRef.current.dispose();
        initGame();
        // Next level's storyline intro
        const st = pd?.stages?.[stageIdxRef.current];
        if (st?.story || st?.name) {
          setStageBanner({ title: st?.name || `Stage ${stageIdxRef.current + 1}`, story: st?.story || '' });
          setTimeout(() => setStageBanner(null), 4200);
        }
      }, 1800);
    } else {
      setGameWon(true);
    }
  };

  // Score gains route through here so a win_score threshold clears the CURRENT level
  // (each level requires win_score points gained WITHIN it; the last level wins the game).
  const stageScoreBaseRef = useRef(0);
  const addScoreStageAware = (gain) => {
    setScore(prev => {
      const next = prev + gain;
      const required = stageScoreBaseRef.current + winScoreRef.current;
      if (winScoreRef.current > 0 && prev < required && next >= required) {
        setTimeout(() => handleStageClear(), 60);
      }
      return next;
    });
  };

  // ---- Boss Victory Burst: particle explosion + freeze-frame beat + shake + boom ----
  const bossVictoryBurst = (scene, pos) => {
    try {
      const c = ensureAudioCtx();
      if (c && !isMutedRef.current) {
        const o = c.createOscillator();
        const g = c.createGain();
        o.type = 'sine';
        o.frequency.setValueAtTime(130, c.currentTime);
        o.frequency.exponentialRampToValueAtTime(28, c.currentTime + 0.65);
        g.gain.setValueAtTime(0.28, c.currentTime);
        g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.75);
        o.connect(g).connect(c.destination);
        o.start();
        o.stop(c.currentTime + 0.8);
      }
      const dt = new BABYLON.DynamicTexture('burst_tex', { width: 32, height: 32 }, scene, false);
      const ctx2 = dt.getContext();
      const grad = ctx2.createRadialGradient(16, 16, 0, 16, 16, 16);
      grad.addColorStop(0, 'rgba(255,255,255,1)');
      grad.addColorStop(0.4, 'rgba(255,180,60,0.9)');
      grad.addColorStop(1, 'rgba(255,60,0,0)');
      ctx2.fillStyle = grad;
      ctx2.fillRect(0, 0, 32, 32);
      dt.update();
      const ps = new BABYLON.ParticleSystem('boss_burst', 400, scene);
      ps.particleTexture = dt;
      ps.emitter = pos.clone();
      if (ps.createSphereEmitter) ps.createSphereEmitter(0.6);
      ps.minEmitPower = 5;
      ps.maxEmitPower = 16;
      ps.minSize = 0.25;
      ps.maxSize = 0.9;
      ps.minLifeTime = 0.4;
      ps.maxLifeTime = 1.2;
      ps.color1 = new BABYLON.Color4(1, 0.85, 0.3, 1);
      ps.color2 = new BABYLON.Color4(1, 0.25, 0.1, 1);
      ps.colorDead = new BABYLON.Color4(0.4, 0, 0, 0);
      ps.blendMode = BABYLON.ParticleSystem.BLENDMODE_ONEONE;
      ps.gravity = new BABYLON.Vector3(0, -4, 0);
      ps.manualEmitCount = 350;
      ps.start();
      setTimeout(() => { try { ps.dispose(); dt.dispose(); } catch { /* gone */ } }, 2400);
      // Slow-motion beat: 300ms freeze-frame, then the explosion detonates with a screen shake
      const engine = scene.getEngine();
      engine.stopRenderLoop();
      containerRef.current?.classList.add('pm-shake');
      setTimeout(() => {
        try { engine.runRenderLoop(() => scene.render()); } catch { /* disposed */ }
        setTimeout(() => containerRef.current?.classList.remove('pm-shake'), 1700);
      }, 300);
    } catch { /* FX must never break the game loop */ }
  };

  // ---- Player ranged combat — real projectiles that destroy enemies (armed games only) ----
  const setupPlayerCombat = (scene, player, gameObjects, settings = {}) => {
    const pd = pdRef.current;
    // Designer override: settings.weapons_enabled toggles combat regardless of the AI's armed flag.
    const armed = settings.weapons_enabled !== undefined ? settings.weapons_enabled : pd?.armed;
    if (!armed || !player) return;
    const projectileUrl = pd.projectile_url || null;
    // Strip flat backgrounds off the projectile sprite ONCE — missiles must never fly
    // around as "a diamond on square paper". Until clean, the themed energy bolt is used.
    let cleanProjectile = null;
    if (projectileUrl) {
      removeBackgroundIfNeeded(projectileUrl)
        .then((u) => { cleanProjectile = u; })
        .catch(() => { cleanProjectile = null; });
    }
    const boltColor = (() => {
      try { return BABYLON.Color3.FromHexString((pd.palette && pd.palette[2]) || '#22FFFF'); }
      catch { return new BABYLON.Color3(0.13, 1, 1); }
    })();
    let lastFire = 0;
    const fire = () => {
      const now = performance.now();
      if (now - lastFire < 250 || !player || player.isDisposed()) return;
      lastFire = now;
      playEnemyCue('shoot');
      let bolt;
      if (cleanProjectile) {
        bolt = BABYLON.MeshBuilder.CreatePlane(`pbolt_${now}`, { size: 0.8, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
        const bm = new BABYLON.StandardMaterial(`pbolt_m_${now}`, scene);
        const tx = new BABYLON.Texture(cleanProjectile, scene, false, true);
        tx.hasAlpha = true;
        bm.diffuseTexture = tx; bm.useAlphaFromDiffuseTexture = true; bm.backFaceCulling = false;
        bm.emissiveColor = new BABYLON.Color3(0.6, 0.6, 0.6);
        bolt.material = bm;
      } else {
        bolt = BABYLON.MeshBuilder.CreateSphere(`pbolt_${now}`, { diameter: 0.55 }, scene);
        const bm = new BABYLON.StandardMaterial(`pbolt_m_${now}`, scene);
        bm.emissiveColor = boltColor;
        bm.disableLighting = true;
        bolt.material = bm;
        try {
          const bgl = scene.getGlowLayerByName?.('glow') || new BABYLON.GlowLayer('glow', scene);
          bgl.addIncludedOnlyMesh(bolt);
        } catch { /* glow is decorative */ }
      }
      bolt.position = player.position.clone();
      // Designer-set projectile origin (e.g. the tip of a gun) — settings.muzzle_offset [x,y,z]
      const mo = settings.muzzle_offset || [0, 0.3, 0];
      bolt.position.x += mo[0] || 0;
      bolt.position.y += mo[1] ?? 0.3;
      bolt.position.z += mo[2] || 0;
      const ang = player.rotation?.y ?? 0;
      let dir = new BABYLON.Vector3(Math.sin(ang), 0, Math.cos(ang));
      if (dir.length() < 0.1) dir = new BABYLON.Vector3(1, 0, 0);
      // Aim at nearest enemy if one is close to the firing line (aim assist)
      let nearest = null, nd = 24;
      gameObjects.enemies.forEach(e => {
        if (!e.mesh || e.mesh.isDisposed()) return;
        const d = BABYLON.Vector3.Distance(player.position, e.mesh.position);
        if (d < nd) { nd = d; nearest = e.mesh; }
      });
      if (nearest) dir = nearest.position.subtract(player.position).normalize();
      let lived = 0;
      const stepper = getFixedStepper(scene);
      const step = (dt) => {
        if (bolt.isDisposed()) { stepper.remove(step); return; }
        bolt.position.addInPlace(dir.scale(30 * dt));
        lived += dt;
        for (const e of gameObjects.enemies) {
          if (!e.mesh || e.mesh.isDisposed()) continue;
          if (BABYLON.Vector3.Distance(bolt.position, e.mesh.position) < 1.1) {
            if (e._hp === undefined) e._hp = e.config?.hp || 20;
            const buff = damageBuffRef.current && performance.now() < damageBuffRef.current.until ? damageBuffRef.current.multiplier : 1;
            e._hp -= 12 * buff;
            playEnemyCue('hit');
            spawnBurst(scene, bolt.position, '#FCD34D', 18, 0.3);
            if (e.config?.boss) setBossInfo(prev => prev ? { ...prev, hp: Math.max(0, e._hp) } : prev);
            if (e._hp <= 0) {
              const dropPos = e.mesh.position.clone();
              e.mesh.dispose();
              playEnemyCue('death');
              const mult = registerKill();
              const gain = (e.config?.boss ? 150 : 25) * mult;
              addScoreStageAware(gain);
              spawnBurst(scene, dropPos, '#F97316', e.config?.boss ? 60 : 34, 0.5);
              scorePop(scene, dropPos, mult > 1 ? `+${gain} ×${mult}` : `+${gain}`, mult > 1 ? '#FB923C' : '#FCD34D');
              if (e.config?.boss) {
                setBossInfo(null);
                bossVictoryBurst(scene, dropPos);
              }
              spawnLoot(scene, dropPos, gameObjects);
            }
            bolt.dispose();
            stepper.remove(step);
            return;
          }
        }
        if (lived > 1.85) { bolt.dispose(); stepper.remove(step); }
      };
      stepper.add(step);
    };
    const onFireKey = (e) => {
      const k = e.key.toLowerCase();
      if (k === 'f' || k === 'j') fire();
    };
    const onPointer = () => fire();
    window.addEventListener('keydown', onFireKey);
    canvasRef.current?.addEventListener('pointerdown', onPointer);
    scene.onDisposeObservable.add(() => {
      window.removeEventListener('keydown', onFireKey);
      canvasRef.current?.removeEventListener('pointerdown', onPointer);
    });
  };

  // ---- Synthesized looping score (WebAudio, no files) ----
  // Weather ambience — soft looping soundscape matching the level's weather:
  // rain = filtered white-noise patter, snow = slow wind swells, embers = low
  // rumble with random high crackle ticks. Respects the mute toggle.
  const weatherAmbStopRef = useRef(null);
  const startWeatherAmbience = (kind) => {
    if (!kind || !['rain', 'snow', 'embers'].includes(kind) || weatherAmbStopRef.current) return;
    const c = ensureAudioCtx();
    if (!c) return;
    const len = c.sampleRate * 2;
    const buf = c.createBuffer(1, len, c.sampleRate);
    const data = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      last = (last + 0.02 * w) / 1.02;
      data[i] = kind === 'rain' ? w : last * 3.5;
    }
    const src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const filt = c.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.value = kind === 'rain' ? 1400 : kind === 'snow' ? 420 : 260;
    const g = c.createGain();
    g.gain.value = 0;
    src.connect(filt).connect(g).connect(c.destination);
    src.start();
    const target = kind === 'rain' ? 0.035 : kind === 'snow' ? 0.03 : 0.025;
    let t = 0;
    const iv = setInterval(() => {
      t += 0.25;
      const lfo = kind === 'snow' ? (0.6 + 0.4 * Math.sin(t * 0.8)) : kind === 'embers' ? (0.75 + 0.25 * Math.sin(t * 2.3)) : 1;
      g.gain.value = isMutedRef.current ? 0 : target * lfo;
    }, 250);
    let crackleIv = null;
    if (kind === 'embers') {
      crackleIv = setInterval(() => {
        if (isMutedRef.current || Math.random() > 0.4) return;
        try {
          const o = c.createOscillator();
          const og = c.createGain();
          o.type = 'square';
          o.frequency.value = 2500 + Math.random() * 3000;
          og.gain.setValueAtTime(0.012, c.currentTime);
          og.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.03);
          o.connect(og).connect(c.destination);
          o.start();
          o.stop(c.currentTime + 0.04);
        } catch { /* ctx interrupted */ }
      }, 380);
    }
    weatherAmbStopRef.current = () => {
      clearInterval(iv);
      if (crackleIv) clearInterval(crackleIv);
      try { src.stop(); g.disconnect(); } catch { /* noop */ }
    };
  };
  const stopWeatherAmbience = () => {
    if (weatherAmbStopRef.current) { weatherAmbStopRef.current(); weatherAmbStopRef.current = null; }
  };

  const startMusic = (profile, genre) => {
    if (musicStopRef.current) return;
    const c = ensureAudioCtx();
    if (!c) return;
    const prof = profile || ({ platformer: 'adventure', fps: 'boss', puzzle: 'chill', rpg: 'dungeon' }[genre] || 'adventure');
    const scales = {
      adventure: [261.6, 329.6, 392.0, 523.3, 392.0, 329.6],
      boss: [220.0, 220.0, 261.6, 220.0, 196.0, 174.6],
      chill: [329.6, 392.0, 440.0, 523.3, 440.0, 392.0],
      dungeon: [196.0, 233.1, 196.0, 174.6, 155.6, 196.0],
    };
    const notes = scales[prof] || scales.adventure;
    const gainNode = c.createGain();
    gainNode.gain.value = 0.03;
    gainNode.connect(c.destination);
    let i = 0;
    const iv = setInterval(() => {
      if (isMutedRef.current) return;
      try {
        const osc = c.createOscillator();
        osc.type = prof === 'boss' ? 'sawtooth' : 'triangle';
        osc.frequency.value = notes[i % notes.length] * (i % 12 >= 6 ? 0.5 : 1);
        const g = c.createGain();
        g.gain.setValueAtTime(0.0001, c.currentTime);
        g.gain.exponentialRampToValueAtTime(1, c.currentTime + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.42);
        osc.connect(g).connect(gainNode);
        osc.start();
        osc.stop(c.currentTime + 0.45);
        i += 1;
      } catch { /* audio ctx interrupted */ }
    }, 450);
    musicStopRef.current = () => { clearInterval(iv); try { gainNode.disconnect(); } catch { /* noop */ } };
  };

  // ---- Enemy sound cues + loot drops (shared audio ctx) ----
  const audioCtxRef = useRef(null);
  const damageBuffRef = useRef({ multiplier: 1, until: 0 });
  // Combo multiplier — rapid consecutive kills (≤3s apart) raise the score multiplier up to ×5.
  const comboRef = useRef({ count: 0, last: 0 });
  const [combo, setCombo] = useState(1);
  const registerKill = () => {
    const now = performance.now();
    const c = comboRef.current;
    c.count = (now - c.last < 3000) ? c.count + 1 : 1;
    c.last = now;
    const mult = Math.min(5, c.count);
    setCombo(mult);
    if (mult > 1) playComboCue(mult);
    return mult;
  };
  // Combo sound ramp — each tier plays a two-note cue at a rising pitch (electric chains).
  const playComboCue = (mult) => {
    const c = ensureAudioCtx();
    if (!c) return;
    const now = c.currentTime;
    const freq = 740 * Math.pow(1.22, mult - 1);
    [0, 0.06].forEach((off, i) => {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq * (i ? 1.5 : 1), now + off);
      g.gain.setValueAtTime(0.0001, now + off);
      g.gain.exponentialRampToValueAtTime(0.07, now + off + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, now + off + 0.12);
      osc.connect(g).connect(c.destination);
      osc.start(now + off);
      osc.stop(now + off + 0.14);
    });
  };
  useEffect(() => {
    const iv = setInterval(() => {
      if (comboRef.current.count > 0 && performance.now() - comboRef.current.last > 3000) {
        comboRef.current.count = 0;
        setCombo(1);
      }
    }, 400);
    return () => clearInterval(iv);
  }, []);
  // ---- Local co-op gamepads ("gamebox") ----
  // Pad 1 drives the hero (merged into the shared input map, works beside keyboard/touch).
  // Pads 2-4 join as extra players: they claim an NPC if one exists, otherwise a tinted
  // hero clone spawns. Y cycles which character that player controls.
  const coopRef = useRef([]);
  const padKeysRef = useRef({});
  const playerMeshRef = useRef(null);
  const introShownRef = useRef(false);

  const claimCoopUnit = (padIndex) => {
    const scene = sceneRef.current;
    if (!scene || scene.isDisposed) return null;
    const npc = scene.meshes.find(mm => mm.metadata?.coopEligible && !mm.isDisposed() && !coopRef.current.some(u => u.mesh === mm));
    if (npc) return { padIndex, mesh: npc, vy: 0, spawn: npc.position.clone(), label: npc.name };
    const player = playerMeshRef.current;
    if (!player || player.isDisposed()) return null;
    const clone = player.clone(`coop_p${padIndex + 1}`);
    clone.position = player.position.add(new BABYLON.Vector3(1.2 * padIndex, 0.4, 0.6));
    const tints = ['#F97316', '#22C55E', '#A855F7'];
    clone.renderOverlay = true;
    clone.overlayColor = BABYLON.Color3.FromHexString(tints[(padIndex - 1) % tints.length]);
    clone.overlayAlpha = 0.35;
    return { padIndex, mesh: clone, vy: 0, spawn: clone.position.clone(), label: `Hero ${padIndex + 1}` };
  };

  const cycleCoopUnit = (unit) => {
    const scene = sceneRef.current;
    if (!scene || scene.isDisposed) return;
    const eligible = scene.meshes.filter(mm => mm.metadata?.coopEligible && !mm.isDisposed() && mm !== unit.mesh && !coopRef.current.some(u => u.mesh === mm));
    if (!eligible.length) { toast.info('No other free characters in this level'); return; }
    if (unit.mesh?.name?.startsWith('coop_p')) unit.mesh.dispose();
    unit.mesh = eligible[0];
    unit.spawn = unit.mesh.position.clone();
    unit.vy = 0;
    toast.success(`🎮 P${unit.padIndex + 1} now controls "${unit.mesh.name}"`);
  };

  const updateCoop = (pad, padIndex) => {
    const scene = sceneRef.current;
    if (!scene || scene.isDisposed) return;
    let unit = coopRef.current.find(u => u.padIndex === padIndex);
    const anyInput = pad.buttons.some(b => b.pressed) || Math.abs(pad.axes[0] || 0) > 0.5 || Math.abs(pad.axes[1] || 0) > 0.5;
    if (!unit) {
      if (!anyInput) return;
      unit = claimCoopUnit(padIndex);
      if (!unit) return;
      coopRef.current.push(unit);
      toast.success(`🎮 Player ${padIndex + 1} joined as "${unit.label}" — press Y to switch character`);
    }
    const m = unit.mesh;
    if (!m || m.isDisposed()) { coopRef.current = coopRef.current.filter(u => u !== unit); return; }
    if (pad.buttons[3]?.pressed && !unit._cycleHeld) { unit._cycleHeld = true; cycleCoopUnit(unit); return; }
    if (!pad.buttons[3]?.pressed) unit._cycleHeld = false;
    const dt = Math.min(0.05, scene.getEngine().getDeltaTime() / 1000);
    const spd = 6;
    const ax = Math.abs(pad.axes[0] || 0) > 0.35 ? pad.axes[0] : (pad.buttons[15]?.pressed ? 1 : pad.buttons[14]?.pressed ? -1 : 0);
    const az = Math.abs(pad.axes[1] || 0) > 0.35 ? -pad.axes[1] : (pad.buttons[12]?.pressed ? 1 : pad.buttons[13]?.pressed ? -1 : 0);
    unit.vy = (unit.vy || 0) - 22 * dt;
    const ray = new BABYLON.Ray(m.position, new BABYLON.Vector3(0, -1, 0), 1.15);
    const hit = scene.pickWithRay(ray, (mm) => mm !== m && mm.isPickable && mm.checkCollisions);
    const grounded = !!(hit && hit.hit);
    if (grounded && unit.vy < 0) unit.vy = 0;
    if (grounded && pad.buttons[0]?.pressed) unit.vy = 8.5;
    m.moveWithCollisions(new BABYLON.Vector3(ax * spd * dt, unit.vy * dt, az * spd * dt));
    if (m.position.y < -25) { m.position = unit.spawn.clone(); unit.vy = 0; }
  };

  useEffect(() => {
    let raf;
    const DEAD = 0.35;
    const poll = () => {
      raf = requestAnimationFrame(poll);
      const pads = (navigator.getGamepads && navigator.getGamepads()) || [];
      const p0 = pads[0];
      const inputMap = inputMapRef.current;
      const mapKey = (key, active) => {
        if (active) { inputMap[key] = true; padKeysRef.current[key] = true; }
        else if (padKeysRef.current[key]) { inputMap[key] = false; padKeysRef.current[key] = false; }
      };
      if (p0) {
        mapKey('a', (p0.axes[0] || 0) < -DEAD || !!p0.buttons[14]?.pressed);
        mapKey('d', (p0.axes[0] || 0) > DEAD || !!p0.buttons[15]?.pressed);
        mapKey('w', (p0.axes[1] || 0) < -DEAD || !!p0.buttons[12]?.pressed);
        mapKey('s', (p0.axes[1] || 0) > DEAD || !!p0.buttons[13]?.pressed);
        mapKey(' ', !!p0.buttons[0]?.pressed);
        mapKey('f', !!(p0.buttons[2]?.pressed || p0.buttons[7]?.pressed));
      }
      for (let i = 1; i < Math.min(4, pads.length); i++) {
        if (pads[i]) updateCoop(pads[i], i);
      }
    };
    raf = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(raf);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ensureAudioCtx = () => {
    if (typeof window === 'undefined') return null;
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
      return audioCtxRef.current;
    } catch { return null; }
  };
  const playEnemyCue = (kind) => {
    const c = ensureAudioCtx();
    if (!c) return;
    const now = c.currentTime;
    const beep = (freq, dur, type = 'sine', gain = 0.06) => {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, now);
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(gain, now + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
      osc.connect(g).connect(c.destination);
      osc.start(now);
      osc.stop(now + dur + 0.02);
    };
    switch (kind) {
      case 'chase':  beep(90, 0.35, 'sawtooth', 0.04); break;              // low growl
      case 'shoot':  beep(880, 0.08, 'square'); beep(1320, 0.06, 'square'); break; // laser blip
      case 'summon': beep(660, 0.18, 'sine'); beep(990, 0.18, 'sine'); beep(1320, 0.22, 'triangle'); break; // shimmer chord
      case 'hit':    beep(220, 0.06, 'square', 0.05); break;               // thud
      case 'death':  beep(180, 0.12, 'sawtooth', 0.07); beep(90, 0.2, 'sawtooth', 0.05); break; // fall
      case 'coin':   beep(1568, 0.08, 'triangle', 0.05); beep(2093, 0.1, 'triangle', 0.05); break;
      case 'heal':   beep(523, 0.1, 'sine'); beep(784, 0.12, 'sine'); break;
      default: break;
    }
  };

  const spawnLoot = (scene, pos, gameObjects) => {
    if (!gameObjects) return;
    gameObjects.loot = gameObjects.loot || [];
    // Rarity roll: 5% gem, 40% health, 55% coin
    const r = Math.random();
    const kind = r < 0.05 ? 'gem' : (r < 0.45 ? 'health' : 'coin');
    let mesh;
    const mat = new BABYLON.StandardMaterial(`loot_m_${Date.now()}`, scene);
    if (kind === 'gem') {
      mesh = BABYLON.MeshBuilder.CreatePolyhedron(`loot_g_${Date.now()}`, { type: 1, size: 0.35 }, scene);
      mat.diffuseColor = new BABYLON.Color3(0.7, 0.2, 0.95);
      mat.emissiveColor = new BABYLON.Color3(0.45, 0.1, 0.7);
      mat.specularColor = new BABYLON.Color3(1, 0.7, 1);
    } else if (kind === 'health') {
      mesh = BABYLON.MeshBuilder.CreateBox(`loot_h_${Date.now()}`, { size: 0.5 }, scene);
      mat.diffuseColor = new BABYLON.Color3(0.2, 0.9, 0.4);
      mat.emissiveColor = new BABYLON.Color3(0.1, 0.4, 0.15);
    } else {
      mesh = BABYLON.MeshBuilder.CreateCylinder(`loot_c_${Date.now()}`, { diameter: 0.55, height: 0.12 }, scene);
      mat.diffuseColor = new BABYLON.Color3(1, 0.85, 0.15);
      mat.emissiveColor = new BABYLON.Color3(0.55, 0.4, 0.05);
    }
    mesh.material = mat;
    const baseY = Math.max(0.5, pos.y);
    mesh.position = new BABYLON.Vector3(pos.x, baseY, pos.z);
    if (kind === 'coin') mesh.rotation.x = Math.PI / 2;
    gameObjects.loot.push({ mesh, kind, baseY, phase: Math.random() * Math.PI * 2 });
  };

  const setupSpawners = (scene, gameObjects, player) => {
    const spawners = gameObjects.spawners || [];
    spawners.forEach((s) => {
      if (!s.mesh || !s.config) return;
      const waves = s.config.waves || [{ count: 3, interval_ms: 2500, enemy: { type: 'enemy', behavior: 'chase', damage: 10 } }];
      // Difficulty curve: 'flat' | 'linear' (default) | 'exp'
      const curve = s.config.difficulty_curve || 'linear';
      const scaleFor = (waveIdx) => {
        if (curve === 'flat') return 1;
        if (curve === 'exp')  return Math.pow(1.35, waveIdx);   // 1, 1.35, 1.82, 2.46 ...
        return 1 + waveIdx * 0.35;                              // linear: 1, 1.35, 1.7, 2.05 ...
      };
      const behaviorPalette = ['chase', 'patrol', 'shoot', 'summon'];

      let waveIdx = 0;
      const runWave = () => {
        if (waveIdx >= waves.length) return;
        const w = waves[waveIdx];
        const scale = scaleFor(waveIdx);
        // Scale count (+1 per 2 waves), damage, speed
        const scaledCount = Math.max(1, Math.round((w.count || 3) * (curve === 'flat' ? 1 : (1 + waveIdx * 0.5))));
        const scaledDamage = Math.round(((w.enemy && w.enemy.damage) || 10) * scale);
        // Wave 3+ starts rotating through the enemy variety pack for spice
        const forcedBehavior = w.enemy && w.enemy.behavior;
        let spawned = 0;
        const step = () => {
          if (!scene || scene.isDisposed) return;
          const enemyMesh = BABYLON.MeshBuilder.CreateCapsule(`spawn_${Date.now()}_${spawned}`, { radius: 0.5 * (1 + waveIdx * 0.05), height: 1.5 }, scene);
          const eMat = new BABYLON.StandardMaterial(`${enemyMesh.name}_mat`, scene);
          // Redder + more emissive with each wave
          eMat.diffuseColor = new BABYLON.Color3(Math.min(1, 0.7 + waveIdx * 0.05), 0.2, 0.2);
          eMat.emissiveColor = new BABYLON.Color3(Math.min(0.6, 0.1 + waveIdx * 0.08), 0, 0);
          enemyMesh.material = eMat;
          const mesh = enemyMesh;
          const base = s.mesh.position;
          const offX = (Math.random() * 2 - 1) * 2;
          const offZ = (Math.random() * 2 - 1) * 2;
          mesh.position = new BABYLON.Vector3(base.x + offX, base.y + 1, base.z + offZ);
          const behavior = forcedBehavior || (waveIdx >= 2 ? behaviorPalette[(spawned + waveIdx) % behaviorPalette.length] : 'chase');
          const enemyObj = { mesh, config: {
            type: 'enemy',
            damage: scaledDamage,
            behavior,
            speed: 0.02 * scale,
            wave: waveIdx,
          }};
          gameObjects.enemies.push(enemyObj);
          // Immediately install the AI so the spawned enemy actually moves
          runEnemyBehavior(scene, enemyObj, player, gameObjects);
          spawned += 1;
          if (spawned < scaledCount) {
            setTimeout(step, Math.max(400, (w.interval_ms || 2500) / (1 + waveIdx * 0.3)));
          } else {
            waveIdx += 1;
            setTimeout(runWave, (w.interval_ms || 2500) * 2);
          }
        };
        step();
      };
      setTimeout(runWave, s.config.delay_ms || 1500);
    });
  };

  // Enemy variety pack — chase / patrol / shoot / summon
  const runEnemyBehavior = (scene, enemy, player, gameObjects) => {
    if (!enemy || !enemy.mesh || !enemy.config) return;
    let behavior = enemy.config.behavior || 'idle';
    let speed = enemy.config.speed || 0.02;
    // Behavior aliases from the AI design vocabulary
    if (behavior === 'ranged') behavior = 'shoot';
    if (behavior === 'swarm') { behavior = 'chase'; speed = speed * 1.8; }
    if (behavior === 'guard') behavior = 'patrol';

    if (behavior === 'boss' && player) {
      // Unique attack pattern: alternates a charging phase with 3-way projectile volleys
      let phaseStart = performance.now();
      let phase = 0;
      getFixedStepper(scene).add((dt) => {
        if (!enemy.mesh || enemy.mesh.isDisposed() || !player || player.isDisposed()) return;
        const now = performance.now();
        if (now - phaseStart > 3500) { phaseStart = now; phase = (phase + 1) % 2; }
        if (phase === 0) {
          const dir = player.position.subtract(enemy.mesh.position);
          const d = dir.length();
          if (d < 28 && d > 2) {
            dir.normalize();
            enemy.mesh.position.addInPlace(dir.scale(speed * 144 * dt));
            enemy.mesh.rotation.y = Math.atan2(dir.x, dir.z);
          }
        } else if (!enemy._lastVolley || now - enemy._lastVolley > 1100) {
          enemy._lastVolley = now;
          if (BABYLON.Vector3.Distance(enemy.mesh.position, player.position) > 26) return;
          playEnemyCue('shoot');
          const base = player.position.subtract(enemy.mesh.position).normalize();
          [-0.35, 0, 0.35].forEach((off) => {
            const dir = BABYLON.Vector3.TransformCoordinates(base, BABYLON.Matrix.RotationY(off));
            const bullet = BABYLON.MeshBuilder.CreateSphere(`bossbullet_${now}_${off}`, { diameter: 0.45 }, scene);
            bullet.position = enemy.mesh.position.clone();
            bullet.position.y += 0.5;
            const bm = new BABYLON.StandardMaterial(`${bullet.name}_m`, scene);
            bm.emissiveColor = new BABYLON.Color3(1, 0.15, 0.15);
            bullet.material = bm;
            let lived = 0;
            const stepper = getFixedStepper(scene);
            const step = (sdt) => {
              if (bullet.isDisposed()) { stepper.remove(step); return; }
              bullet.position.addInPlace(dir.scale(19.2 * sdt));
              lived += sdt;
              if (BABYLON.Vector3.Distance(bullet.position, player.position) < 1.1) {
                if (!playerInvulnerable()) {
                  const dmg = (enemy.config.damage || 20) / 2;
                  spawnBurst(scene, player.position, '#EF4444', 18, 0.3);
                  scorePop(scene, player.position, `-${dmg} HP`, '#F87171');
                  setHealth(prev => { const nv = prev - dmg; if (nv <= 0) setGameOver(true); return Math.max(0, nv); });
                }
                bullet.dispose();
                stepper.remove(step);
              } else if (lived > 1.7) {
                bullet.dispose();
                stepper.remove(step);
              }
            };
            stepper.add(step);
          });
        }
      });
    } else if (behavior === 'chase' && player) {
      let lastGrowl = 0;
      getFixedStepper(scene).add((dt) => {
        if (!enemy.mesh || enemy.mesh.isDisposed() || !player || player.isDisposed()) return;
        const direction = player.position.subtract(enemy.mesh.position);
        const distance = direction.length();
        if (distance < 20 && distance > 1.5) {
          direction.normalize();
          enemy.mesh.position.addInPlace(direction.scale(speed * 60 * dt));
          enemy.mesh.rotation.y = Math.atan2(direction.x, direction.z);
          // Growl once every 1.8s when actively chasing within earshot
          const now = performance.now();
          if (distance < 12 && now - lastGrowl > 1800) { lastGrowl = now; playEnemyCue('chase'); }
        }
      });
    } else if (behavior === 'patrol') {
      // Back-and-forth on X axis, same code path as scenery patrols
      addPatrolBehavior(scene, enemy.mesh, [enemy.mesh.position.x, enemy.mesh.position.y, enemy.mesh.position.z], { patrol_dist: 4, patrol_speed: speed * 1.5 });
    } else if (behavior === 'shoot' && player) {
      // Fire a small emissive sphere ~every 1.5s toward the player
      let last = performance.now();
      scene.registerBeforeRender(() => {
        if (!enemy.mesh || enemy.mesh.isDisposed() || !player || player.isDisposed()) return;
        const now = performance.now();
        if (now - last < 1500) return;
        last = now;
        const dist = BABYLON.Vector3.Distance(enemy.mesh.position, player.position);
        if (dist > 22) return;
        playEnemyCue('shoot');
        const bullet = BABYLON.MeshBuilder.CreateSphere(`bullet_${now}`, { diameter: 0.35 }, scene);
        bullet.position = enemy.mesh.position.clone();
        const mat = new BABYLON.StandardMaterial(`${bullet.name}_m`, scene);
        mat.emissiveColor = new BABYLON.Color3(1, 0.6, 0.1);
        bullet.material = mat;
        const dir = player.position.subtract(enemy.mesh.position).normalize();
        const bulletSpeed = 21; // units/sec (legacy 0.35/frame @60fps)
        let lived = 0;
        const stepper = getFixedStepper(scene);
        const bstep = (dt) => {
          if (bullet.isDisposed()) { stepper.remove(bstep); return; }
          bullet.position.addInPlace(dir.scale(bulletSpeed * dt));
          lived += dt;
          if (lived > 1.5 || BABYLON.Vector3.Distance(bullet.position, player.position) < 1) {
            if (BABYLON.Vector3.Distance(bullet.position, player.position) < 1 && !playerInvulnerable()) {
              const dmg = (enemy.config.damage || 10) / 2;
              spawnBurst(scene, player.position, '#EF4444', 18, 0.3);
              scorePop(scene, player.position, `-${dmg} HP`, '#F87171');
              setHealth(prev => { const nv = prev - dmg; if (nv <= 0) setGameOver(true); return Math.max(0, nv); });
            }
            bullet.dispose();
            stepper.remove(bstep);
          }
        };
        stepper.add(bstep);
      });
    } else if (behavior === 'summon' && player) {
      // Periodically spawn a small chaser minion; up to 3 alive at once
      const minions = [];
      let last = performance.now();
      scene.registerBeforeRender(() => {
        if (!enemy.mesh || enemy.mesh.isDisposed()) return;
        // Prune dead minions
        for (let i = minions.length - 1; i >= 0; i--) if (!minions[i].mesh || minions[i].mesh.isDisposed()) minions.splice(i, 1);
        const now = performance.now();
        if (now - last < 4000 || minions.length >= 3) return;
        last = now;
        playEnemyCue('summon');
        const m = BABYLON.MeshBuilder.CreateSphere(`minion_${now}`, { diameter: 0.7 }, scene);
        const mm = new BABYLON.StandardMaterial(`${m.name}_m`, scene);
        mm.diffuseColor = new BABYLON.Color3(0.9, 0.4, 0.9);
        mm.emissiveColor = new BABYLON.Color3(0.3, 0.05, 0.3);
        m.material = mm;
        m.position = enemy.mesh.position.clone(); m.position.y += 1;
        const minionObj = { mesh: m, config: { type: 'enemy', damage: 4, behavior: 'chase', speed: 0.035 } };
        (gameObjects?.enemies || []).push(minionObj);
        minions.push(minionObj);
        // Recurse — minions themselves chase
        runEnemyBehavior(scene, minionObj, player, gameObjects);
      });
    }
  };

  const setupEnemyAI = (scene, enemies, player, gameObjects) => {
    enemies.forEach((enemy) => runEnemyBehavior(scene, enemy, player, gameObjects));
  };

  const setupVR = async (scene, canvas) => {
    try {
      const vrHelper = await scene.createDefaultXRExperienceAsync({
        floorMeshes: scene.meshes.filter(m => m.name.includes('ground') || m.name.includes('floor'))
      });
      
      if (vrHelper.baseExperience) {
        setVrSupported(true);
        vrHelperRef.current = vrHelper;
        
        vrHelper.baseExperience.onStateChangedObservable.add((state) => {
          setInVR(state === BABYLON.WebXRState.IN_XR);
        });
      }
    } catch (e) {
      console.log('VR not supported:', e.message);
      setVrSupported(false);
    }
  };

  const enterVR = async () => {
    if (vrHelperRef.current && vrHelperRef.current.baseExperience) {
      await vrHelperRef.current.baseExperience.enterXRAsync('immersive-vr', 'local-floor');
    }
  };

  const fetchProject = async () => {
    const fromOwn = () => axios.get(`${API}/projects/${projectId}`, { withCredentials: true });
    const fromGallery = () => axios.get(`${API}/gallery/${projectId}`);
    // Guests hit the public endpoint first — avoids a guaranteed 401 on every public play.
    const [first, second] = userRef.current ? [fromOwn, fromGallery] : [fromGallery, fromOwn];
    try {
      const res = await first();
      setProject(res.data);
    } catch (error) {
      try {
        const res = await second();
        setProject(res.data);
      } catch (e) {
        navigate('/gallery');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const restartGame = () => {
    setScore(0);
    stageScoreBaseRef.current = 0;
    setHealth(startingHealthRef.current || 100);
    setCollectiblesCount(0);
    setGameOver(false);
    setGameWon(false);
    setIsPaused(false);
    stageIdxRef.current = 0;
    setStageIdx(0);
    stageClearingRef.current = false;
    playStartRef.current = performance.now();
    telemetrySentRef.current = { started: true };
    if (musicStopRef.current) { musicStopRef.current(); musicStopRef.current = null; }
    stopWeatherAmbience();
    
    if (engineRef.current) {
      engineRef.current.dispose();
    }
    initGame();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#06B6D4] animate-spin" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`h-[100dvh] bg-[#050505] flex flex-col overflow-hidden ${bossIntro ? 'pm-shake' : ''}`} data-testid="gameplay-page">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={cycleCamera} className="text-white/70 hover:text-white" data-testid="gameplay-camera-btn" title="Change camera angle">
              <Eye className="w-4 h-4 mr-1" /> {camMode === 'auto' ? 'Camera' : camMode.replace('_', ' ')}
            </Button>
            {!isEmbed && (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-white/70 hover:text-white" data-testid="gameplay-exit-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Exit
                </Button>
                <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="text-white/70 hover:text-white w-8 h-8" title="Home" data-testid="gameplay-home-btn">
                  <Home className="w-4 h-4" />
                </Button>
              </>
            )}
            <h1 className="text-lg font-bold text-white">{project?.name}</h1>
            {project?.remixed_from?.project_id && (
              <button
                onClick={() => navigate(`/play/${project.remixed_from.project_id}`)}
                className="text-[11px] text-[#06B6D4]/80 hover:text-[#06B6D4] block truncate max-w-[280px] text-left"
                data-testid="play-remix-lineage"
              >
                ⟳ remixed from "{project.remixed_from.name}" by {project.remixed_from.author_name || 'Unknown'}
              </button>
            )}
          </div>

          {/* Game HUD — advanced-tech styling */}
          <div className="flex items-center gap-3">
          {/* Health */}
          <div className="flex items-center gap-2 bg-[#050B14]/80 border border-[#06B6D4]/25 px-3 py-1.5 rounded-sm backdrop-blur-md" data-testid="hud-health">
            <Heart className="w-4 h-4 text-red-500" />
            <div className="w-24 h-2 bg-gray-800 rounded-sm overflow-hidden border border-white/10">
              <div 
                className="h-full bg-gradient-to-r from-red-600 to-red-400 transition-all duration-300" 
                style={{ width: `${Math.max(0, Math.min(100, (health / (startingHealthRef.current || 100)) * 100))}%` }}
              />
            </div>
            <span className="text-white text-sm font-mono" data-testid="hud-hp-value">{health}/{startingHealthRef.current || 100}</span>
          </div>

            {/* Score */}
            <div className="flex items-center gap-2 bg-[#050B14]/80 border border-[#06B6D4]/25 px-3 py-1.5 rounded-sm backdrop-blur-md">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="text-[9px] text-[#06B6D4]/70 font-mono tracking-widest hidden sm:inline">SCORE</span>
              <span className="text-white font-bold font-mono">{score}</span>
            </div>

            {/* Combo multiplier */}
            {combo > 1 && (
              <div className="flex items-center gap-1 bg-orange-500/20 border border-orange-400/60 px-3 py-1.5 rounded-sm backdrop-blur-md animate-pulse" data-testid="hud-combo">
                <span className="text-orange-300 text-xs font-mono font-bold tracking-widest">🔥 ×{combo} COMBO</span>
              </div>
            )}

            {/* Collectibles */}
            <div className="flex items-center gap-2 bg-[#050B14]/80 border border-[#06B6D4]/25 px-3 py-1.5 rounded-sm backdrop-blur-md">
              <Trophy className="w-4 h-4 text-[#06B6D4]" />
              <span className="text-white font-mono">{collectiblesCount}/{totalCollectibles}</span>
            </div>

            {/* Stage tracker */}
            {(project?.game_data?.projectDefinition?.stages?.length || 0) > 1 && (
              <div className="flex items-center gap-2 bg-[#050B14]/80 border border-[#06B6D4]/25 px-3 py-1.5 rounded-sm backdrop-blur-md" data-testid="hud-stage">
                <span className="text-[#06B6D4] text-xs font-mono font-bold">STAGE {stageIdx + 1}/{project.game_data.projectDefinition.stages.length}</span>
              </div>
            )}

            {/* Damage buff indicator */}
            {buffActive && (
              <div className="flex items-center gap-2 bg-purple-500/30 border border-purple-400 px-3 py-1.5 rounded-full animate-pulse" data-testid="damage-buff">
                <span className="text-purple-200 text-xs font-mono font-bold">◆ 2× DMG</span>
              </div>
            )}

            {/* Controls */}
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => setShowInstructions(!showInstructions)} className="text-white/70 hover:text-white">
                <Info className="w-5 h-5" />
              </Button>
              {vrSupported && (
                <Button variant="ghost" size="icon" onClick={enterVR} className="text-white/70 hover:text-white">
                  <Eye className="w-5 h-5" />
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={() => setIsPaused(!isPaused)} className="text-white/70 hover:text-white">
                {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={toggleFullscreen} className="text-white/70 hover:text-white">
                {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Pre-game briefing — plot + how to play; the game is frozen until OK */}
      {startGate && !isEmbed && project && (
        <div className="absolute inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="game-briefing-gate">
          <div className="max-w-lg w-full bg-[#0A0F1A] border border-[#06B6D4]/40 rounded-2xl p-6 shadow-[0_0_40px_rgba(6,182,212,0.2)]">
            <h2 className="text-2xl font-black text-white mb-1">{project.name}</h2>
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#06B6D4] mb-3">{project.genre} — mission briefing</p>
            <p className="text-sm text-zinc-300 leading-relaxed mb-3" data-testid="briefing-plot">
              {project.game_data?.projectDefinition?.description || project.description || 'Explore the world, defeat what stands in your way, and reach the goal.'}
            </p>
            <div className="text-xs text-zinc-400 space-y-1 mb-4 font-mono">
              <p>🎯 {(
                { destroy_all: 'Destroy every enemy to win.',
                  survive: 'Survive until the timer runs out.',
                  collect_and_reach: 'Collect the pickups, then reach the goal.',
                  rescue: 'Free the captives and reach the goal.' }
              )[project.game_data?.projectDefinition?.objective] || 'Collect the pickups and reach the goal.'}</p>
              <p>{isTouch
                ? '🕹 Move: on-screen arrow pad · Jump: JUMP button' + (project.game_data?.projectDefinition?.armed ? ' · Fire: tap the game' : '') + ' · 🎮 gamepads supported'
                : `⌨ Move: WASD · Jump: Space${project.game_data?.projectDefinition?.armed ? ' · Fire: F / Click' : ''} · 🎮 gamepads supported`}</p>
              {(project.game_data?.projectDefinition?.stages?.length || 0) > 1 && (
                <p>🗺 {project.game_data.projectDefinition.stages.length} levels — clear each to advance.</p>
              )}
            </div>
            <Button
              className="w-full bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90 font-bold disabled:opacity-70"
              disabled={!assetsReady}
              onClick={() => { startGateRef.current = false; setStartGate(false); grantSpawnGrace(); }}
              data-testid="briefing-ok-btn"
            >
              {assetsReady ? "OK — I'm ready, start the game" : `Loading world… ${loadPct}%`}
            </Button>
          </div>
        </div>
      )}

      {/* Instructions Panel — advanced-tech mission briefing */}
      {showInstructions && !gameOver && !gameWon && (
        <div className="absolute top-20 left-4 z-20 bg-[#050B14]/95 backdrop-blur-md border border-[#06B6D4]/40 rounded-lg p-4 max-w-xs shadow-[0_0_30px_rgba(6,182,212,0.15)]" data-testid="controls-panel">
          <h3 className="text-[#67E8F9] font-bold mb-3 flex items-center gap-2 font-mono text-sm tracking-widest uppercase">
            <Gamepad2 className="w-4 h-4 text-[#06B6D4]" /> Mission Controls
          </h3>
          {(() => {
            const Kbd = ({ children }) => (
              <kbd className="px-1.5 py-0.5 bg-[#0E1A2B] border border-[#06B6D4]/40 rounded text-[10px] font-mono text-[#67E8F9] shadow-[0_1px_0_rgba(6,182,212,0.3)]">{children}</kbd>
            );
            return (
              <div className="space-y-2 text-sm">
                {isTouch ? (
                  <>
                    <div className="flex justify-between items-center text-white/80">
                      <span>Move</span>
                      <span className="text-[#67E8F9] text-xs font-mono">◀ ▲ ▼ ▶ arrow pad</span>
                    </div>
                    <div className="flex justify-between items-center text-white/80">
                      <span>Jump</span>
                      <span className="text-[#67E8F9] text-xs font-mono">JUMP button</span>
                    </div>
                    <div className="flex justify-between items-center text-white/80">
                      <span>Look Around</span>
                      <span className="text-[#67E8F9] text-xs font-mono">drag the screen</span>
                    </div>
                    {project?.game_data?.projectDefinition?.armed && (
                      <div className="flex justify-between items-center text-white/80">
                        <span>Fire</span>
                        <span className="text-[#67E8F9] text-xs font-mono">tap the game</span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                <div className="flex justify-between items-center text-white/80">
                  <span>Move</span>
                  <span className="flex gap-1"><Kbd>W</Kbd><Kbd>A</Kbd><Kbd>S</Kbd><Kbd>D</Kbd></span>
                </div>
                <div className="flex justify-between items-center text-white/80">
                  <span>Jump</span>
                  <Kbd>SPACE</Kbd>
                </div>
                <div className="flex justify-between items-center text-white/80">
                  <span>Look Around</span>
                  <Kbd>MOUSE</Kbd>
                </div>
                {project?.game_data?.projectDefinition?.armed && (
                  <div className="flex justify-between items-center text-white/80">
                    <span>Fire</span>
                    <span className="flex gap-1"><Kbd>F</Kbd><Kbd>J</Kbd><Kbd>CLICK</Kbd></span>
                  </div>
                )}
                  </>
                )}
                <div className="flex justify-between items-center gap-4 text-white/80">
                  <span className="shrink-0">Combo bonus</span>
                  <span className="text-orange-300 text-[10px] font-mono text-right">chain kills ≤3s → ×5</span>
                </div>
                <div className="flex justify-between items-start gap-4 text-white/80">
                  <span className="shrink-0">Gamepads</span>
                  <span className="text-[#06B6D4] text-[10px] font-mono text-right leading-relaxed">P1 = hero · P2-P4 join in<br />(Y switches character)</span>
                </div>
                {vrSupported && (
                  <div className="flex justify-between items-center text-white/80">
                    <span>Enter VR</span>
                    <span className="text-[#06B6D4] text-xs">Click VR icon</span>
                  </div>
                )}
              </div>
            );
          })()}
          <div className="mt-4 pt-3 border-t border-[#06B6D4]/20">
            <h4 className="text-[#06B6D4]/70 text-[10px] uppercase mb-2 font-mono tracking-widest">◢ Objectives</h4>
            <ul className="text-sm text-white/80 space-y-1" data-testid="objective-hint">
              {(() => {
                const obj = project?.game_data?.projectDefinition?.objective || 'collect_and_reach';
                const hints = {
                  collect_and_reach: ['• Collect ALL items', '• Then reach the goal'],
                  escape: ['• Reach the exit to escape'],
                  destroy_all: ['• Destroy every enemy'],
                  survive: [`• Survive for ${project?.game_data?.projectDefinition?.survive_seconds || 45}s`],
                  rescue: ['• Reach the rescue point'],
                };
                return (hints[obj] || hints.collect_and_reach).map((h) => <li key={h}>{h}</li>);
              })()}
              <li>• Avoid enemies & hazards</li>
            </ul>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowInstructions(false)}
            className="w-full mt-3 text-white/60 hover:text-white"
          >
            Got it!
          </Button>
        </div>
      )}

      {/* Boss Intro — screen shake + name reveal */}
      {bossIntro && (
        <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none" data-testid="boss-intro">
          <div className="text-center pm-boss-reveal">
            <p className="text-red-500 font-mono text-sm tracking-[0.5em] uppercase animate-pulse">⚠ Warning ⚠</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-3 drop-shadow-[0_0_30px_rgba(220,38,38,0.9)]">
              {bossIntro}
            </h2>
            <p className="text-red-400/80 font-mono text-xs tracking-widest uppercase mt-3">approaching</p>
          </div>
        </div>
      )}

      {/* Boss HP Bar */}
      {bossInfo && !gameOver && !gameWon && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 w-[420px] max-w-[80vw]" data-testid="boss-hp-bar">
          <div className="text-center text-red-400 font-black text-sm tracking-widest uppercase mb-1 drop-shadow">☠ {bossInfo.name}</div>
          <div className="h-3 bg-black/70 border border-red-500/40 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-red-600 to-orange-500 transition-all duration-300"
              style={{ width: `${Math.max(0, (bossInfo.hp / bossInfo.max) * 100)}%` }} />
          </div>
        </div>
      )}

      {/* Stage Cleared Banner */}
      {stageBanner && (
        <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none" data-testid="stage-banner">
          <div className="bg-black/85 border border-[#06B6D4]/50 rounded-2xl px-12 py-8 text-center max-w-2xl">
            <h2 className="text-4xl font-black text-[#06B6D4]">{typeof stageBanner === 'string' ? stageBanner : stageBanner.title}</h2>
            {typeof stageBanner === 'object' && stageBanner.story ? (
              <p className="text-white/85 mt-4 text-lg leading-relaxed" data-testid="stage-story">{stageBanner.story}</p>
            ) : (
              <p className="text-zinc-400 mt-2">Loading next stage…</p>
            )}
          </div>
        </div>
      )}

      {/* Game Over Overlay */}
      {gameOver && (
        <div className="absolute inset-0 z-30 bg-black/90 flex items-center justify-center">
          <div className="text-center">
            <Skull className="w-20 h-20 text-red-500 mx-auto mb-4" />
            <h2 className="text-4xl font-black text-white mb-2">GAME OVER</h2>
            <p className="text-zinc-400 mb-6">Final Score: {score}</p>
            <div className="flex gap-4 justify-center">
              <Button onClick={restartGame} className="bg-[#06B6D4] text-black">
                <RefreshCw className="w-4 h-4 mr-2" /> Try Again
              </Button>
              <Button variant="outline" onClick={() => navigate(-1)} className="border-white/20 text-white">
                Exit
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Win Overlay */}
      {gameWon && (
        <div className="absolute inset-0 z-30 bg-black/90 flex items-center justify-center p-4">
          <div className="bg-[#0A0A0C] border border-[#06B6D4]/30 rounded-2xl overflow-hidden max-w-sm w-full shadow-[0_0_60px_rgba(6,182,212,0.15)]" data-testid="victory-recap-card">
            {project?.thumbnail && (
              <div className="h-36 overflow-hidden relative">
                <img src={project.thumbnail} alt={project?.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] to-transparent" />
              </div>
            )}
            <div className="p-6 text-center -mt-6 relative">
              <Trophy className="w-12 h-12 text-yellow-400 mx-auto mb-2 drop-shadow-[0_0_15px_rgba(250,204,21,0.6)]" />
              <h2 className="text-3xl font-black text-white">VICTORY!</h2>
              <p className="text-zinc-400 text-sm mb-5 truncate">{project?.name}</p>
              <div className="grid grid-cols-2 gap-2 mb-5">
                <div className="bg-[#121214] border border-[#1E1E24] rounded-lg py-3">
                  <p className="text-2xl font-black text-[#06B6D4]" data-testid="recap-score">{score}</p>
                  <p className="text-[10px] text-zinc-500 uppercase font-mono">Score</p>
                </div>
                <div className="bg-[#121214] border border-[#1E1E24] rounded-lg py-3">
                  <p className="text-2xl font-black text-white" data-testid="recap-time">
                    {Math.floor(winElapsed / 60)}:{String(winElapsed % 60).padStart(2, '0')}
                  </p>
                  <p className="text-[10px] text-zinc-500 uppercase font-mono">Time</p>
                </div>
                <div className="bg-[#121214] border border-[#1E1E24] rounded-lg py-3">
                  <p className="text-2xl font-black text-emerald-400" data-testid="recap-stages">
                    {(project?.game_data?.projectDefinition?.stages?.length || 1)}/{(project?.game_data?.projectDefinition?.stages?.length || 1)}
                  </p>
                  <p className="text-[10px] text-zinc-500 uppercase font-mono">Stages</p>
                </div>
                <div className="bg-[#121214] border border-[#1E1E24] rounded-lg py-3">
                  <p className="text-2xl font-black text-yellow-400" data-testid="recap-items">{collectiblesCount}/{totalCollectibles}</p>
                  <p className="text-[10px] text-zinc-500 uppercase font-mono">Items</p>
                </div>
              </div>
              <Button
                onClick={async () => {
                  const shareUrl = project?.published
                    ? `${process.env.REACT_APP_BACKEND_URL}/api/share/${projectId}`
                    : `${window.location.origin}/play/${projectId}`;
                  const text = `I beat "${project?.name}" on PlayMold — score ${score} in ${Math.floor(winElapsed / 60)}:${String(winElapsed % 60).padStart(2, '0')}! Can you do better?`;
                  if (navigator.share) {
                    try { await navigator.share({ title: project?.name, text, url: shareUrl }); return; } catch { /* fall back to copy */ }
                  }
                  try {
                    await navigator.clipboard.writeText(`${text} ${shareUrl}`);
                    toast.success('Victory card copied — paste it anywhere!');
                  } catch {
                    toast.error('Could not copy link');
                  }
                }}
                className="w-full bg-[#06B6D4] text-black font-bold mb-2"
                data-testid="recap-share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" /> Share My Victory
              </Button>
              <div className="flex gap-2">
                <Button onClick={restartGame} variant="outline" className="flex-1 border-white/20 text-white" data-testid="recap-replay-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Play Again
                </Button>
                <Button variant="outline" onClick={() => navigate('/gallery')} className="flex-1 border-white/20 text-white">
                  More Games
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pause Overlay */}
      {isPaused && !gameOver && !gameWon && (
        <div className="absolute inset-0 z-30 bg-black/80 flex items-center justify-center">
          <div className="text-center">
            <Pause className="w-16 h-16 text-[#06B6D4] mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-6">PAUSED</h2>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => setIsPaused(false)} className="bg-[#06B6D4] text-black">
                <Play className="w-4 h-4 mr-2" /> Resume
              </Button>
              <Button variant="outline" onClick={() => navigate(-1)} className="border-white/20 text-white">
                Exit
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* On-screen touch controls — shown only on touch devices; same input map as keyboard */}
      {isTouch && !showInstructions && !gameOver && !gameWon && !isPaused && (
        <div className="absolute inset-x-0 bottom-0 z-20 flex items-end justify-between px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pointer-events-none select-none" data-testid="touch-controls">
          <div className="flex gap-2 pointer-events-auto">
            <button {...touchHold('a')} data-testid="touch-left-btn" aria-label="Move left"
              className="w-14 h-14 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-white text-xl active:bg-[#06B6D4]/40">◀</button>
            <button {...touchHold('d')} data-testid="touch-right-btn" aria-label="Move right"
              className="w-14 h-14 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-white text-xl active:bg-[#06B6D4]/40">▶</button>
            <button {...touchHold('w')} data-testid="touch-up-btn" aria-label="Move up / climb"
              className="w-14 h-14 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-white text-xl active:bg-[#06B6D4]/40">▲</button>
            <button {...touchHold('s')} data-testid="touch-down-btn" aria-label="Move down"
              className="w-14 h-14 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-white text-xl active:bg-[#06B6D4]/40">▼</button>
          </div>
          <div className="pointer-events-auto">
            <button {...touchHold(' ')} data-testid="touch-jump-btn" aria-label="Jump"
              className="w-20 h-20 rounded-full bg-[#06B6D4]/25 border-2 border-[#06B6D4]/60 backdrop-blur-sm text-[#67E8F9] text-sm font-bold tracking-wider active:bg-[#06B6D4]/50">JUMP</button>
          </div>
        </div>
      )}

      {/* Game Canvas */}
      <canvas 
        ref={canvasRef} 
        className="flex-1 w-full outline-none touch-none max-w-[100vw]"
        data-testid="game-canvas"
      />
    </div>
  );
}
